import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '@/App';
import { BarChart3, LogOut, Users, ShoppingCart, TrendingUp, Calendar, Download, Key, ArrowLeft, Shield, AlertCircle, CheckCircle, XCircle, Clock, Trash2, Mail, CreditCard, MessageSquare, Star, User, Settings, Eye, Unlink, RefreshCw } from 'lucide-react';
import ConfirmationModal from './ConfirmationModal';
import EmailComposerModal from './EmailComposerModal';
import BulkEmailModal from './BulkEmailModal';

const AdminPanel = () => {
  const [orders, setOrders] = useState([]);
  const [users, setUsers] = useState([]);
  const [licenses, setLicenses] = useState([]);
  const [betaTrials, setBetaTrials] = useState([]);
  const [feedback, setFeedback] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Software Management state
  const [s3Files, setS3Files] = useState([]);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [dragActive, setDragActive] = useState(false);
  
  // Notification state
  const [notification, setNotification] = useState(null);
  
  // Confirmation modal state
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [subscriptionCancellationType, setSubscriptionCancellationType] = useState('immediate'); // 'immediate' or 'end_of_cycle'
  const [activeTab, setActiveTab] = useState('overview');
  const [actionLoading, setActionLoading] = useState({});
  const [searchFilters, setSearchFilters] = useState({
    orders: { orderId: '', date: '', plan: '', licenseStatus: '', customer: '', paymentId: '', subscriptionStatus: '' },
    topCustomers: { email: '' },
    licenses: { licenseKey: '', status: '', orderId: '', customer: '', planType: '' },
    users: { email: '', role: '', emailVerified: '' },
    betaTrials: { email: '', licenseKey: '' },
    feedback: { customer: '', rating: '', date: '' }
  });
  const [userActionLoading, setUserActionLoading] = useState({});
  
  // Email composer modal state
  const [emailComposerModal, setEmailComposerModal] = useState({
    isOpen: false,
    customerEmail: '',
    customerName: '',
    orderData: null
  });

  // Bulk email modal state
  const [bulkEmailModalOpen, setBulkEmailModalOpen] = useState(false);

  // PAN Management state
  const [panUsageStats, setPanUsageStats] = useState({
    totalLicenses: 0,
    licensesWithPANs: 0,
    totalBoundPANs: 0,
    panUtilization: 0
  });
  const [panManagementModal, setPanManagementModal] = useState({
    isOpen: false,
    licenseKey: '',
    licenseData: null,
    boundPANs: []
  });
  
  // Pagination state
  const [pagination, setPagination] = useState({
    orders: { currentPage: 1, itemsPerPage: 10 },
    users: { currentPage: 1, itemsPerPage: 10 },
    licenses: { currentPage: 1, itemsPerPage: 10 },
    betaTrials: { currentPage: 1, itemsPerPage: 10 },
    topCustomers: { currentPage: 1, itemsPerPage: 10 },
    feedback: { currentPage: 1, itemsPerPage: 10 }
  });
  
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();

  // Styled notification function
  const showNotification = (type, title, message) => {
    setNotification({ type, title, message });
    setTimeout(() => setNotification(null), 6000); // Auto-hide after 6 seconds
  };

  useEffect(() => {
    if (!user?.is_admin) {
      navigate('/dashboard');
      return;
    }
    
    // Fetch initial data only once on mount
    fetchData();
  }, []); // Empty dependency array - only run once on mount

  // Separate effect to handle user authentication changes
  useEffect(() => {
    if (!user?.is_admin) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  const fetchData = async () => {
    try {
      const [ordersResponse, usersResponse, licensesResponse, betaTrialsResponse, feedbackResponse, s3FilesResponse, panUsageResponse] = await Promise.all([
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/admin/orders`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }),
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/admin/users`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }),
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/admin/licenses`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }),
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/admin/beta-trials`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }),
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/admin/feedback`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }),
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/admin/s3/list-files`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }),
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/admin/pan/usage`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        })
      ]);
      
      setOrders(ordersResponse.data);
      setUsers(usersResponse.data);
      setLicenses(licensesResponse.data.licenses || []);
      setBetaTrials(betaTrialsResponse.data.beta_trials || []);
      setFeedback(feedbackResponse.data || []);
      setS3Files(s3FilesResponse.data.files || []);
      setPanUsageStats(panUsageResponse.data || {
        totalLicenses: 0,
        licensesWithPANs: 0,
        totalBoundPANs: 0,
        panUtilization: 0
      });
    } catch (error) {
      console.error('Error fetching admin data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Software Management Functions
  const handleFileUpload = async (file) => {
    setUploadLoading(true);
    setUploadProgress(0);
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/s3/upload`,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'multipart/form-data'
          },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            setUploadProgress(percentCompleted);
          }
        }
      );
      
      // Refresh file list
      const s3Response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/s3/list-files`,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }
      );
      setS3Files(s3Response.data.files || []);
      
      showNotification(
        'success', 
        'Upload Successful! 🚀', 
        `File "${response.data.file_info.original_filename}" uploaded successfully to S3 bucket.`
      );
    } catch (error) {
      console.error('Upload failed:', error);
      showNotification(
        'error', 
        'Upload Failed', 
        error.response?.data?.detail || error.message || 'Failed to upload file. Please try again.'
      );
    } finally {
      setUploadLoading(false);
      setUploadProgress(0);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileUpload(files[0]);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragActive(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragActive(false);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      handleFileUpload(file);
    }
  };

  const handleDeleteFile = async (filename) => {
    try {
      await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/s3/delete/${encodeURIComponent(filename)}`,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }
      );
      
      // Refresh file list
      const s3Response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/s3/list-files`,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }
      );
      setS3Files(s3Response.data.files || []);
      
      showNotification('success', 'File Deleted', 'File deleted successfully from S3 bucket.');
    } catch (error) {
      console.error('Delete failed:', error);
      showNotification(
        'error', 
        'Delete Failed', 
        error.response?.data?.detail || error.message || 'Failed to delete file. Please try again.'
      );
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getStatusClass = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getLicenseStatusClass = (status) => {
    switch (status) {
      case 'activated':
        return 'bg-green-100 text-green-800';
      case 'unactivated':
        return 'bg-yellow-100 text-yellow-800';
      case 'expired':
        return 'bg-red-100 text-red-800';
      case 'deactivated':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getLicenseStatusIcon = (status) => {
    switch (status) {
      case 'activated':
        return <CheckCircle className="w-4 h-4" />;
      case 'unactivated':
        return <Clock className="w-4 h-4" />;
      case 'expired':
        return <XCircle className="w-4 h-4" />;
      case 'deactivated':
        return <AlertCircle className="w-4 h-4" />;
      default:
        return <AlertCircle className="w-4 h-4" />;
    }
  };

  // Generate email customer mailto link (kept for backward compatibility)
  const generateCustomerEmailLink = (order, user) => {
    if (!user?.email) return '#';
    
    const customerEmail = user.email;
    const subject = encodeURIComponent(`Order ${formatOrderId(order.id)} - ${order.subscription_type.charAt(0).toUpperCase() + order.subscription_type.slice(1)} Plan Support`);
    
    const supportEmail = process.env.REACT_APP_SUPPORT_EMAIL || 'support@portfolioauditor.com';
    return `mailto:${customerEmail}?from=${supportEmail}&subject=${subject}`;
  };

  // Open email composer modal
  const openEmailComposer = (order, user) => {
    if (!user?.email) {
      showNotification('error', 'Error', 'User email not found');
      return;
    }

    setEmailComposerModal({
      isOpen: true,
      customerEmail: user.email,
      customerName: user.name || 'Customer',
      orderData: order
    });
  };

  // Close email composer modal
  const closeEmailComposer = () => {
    setEmailComposerModal({
      isOpen: false,
      customerEmail: '',
      customerName: '',
      orderData: null
    });
  };

  // Handle email sent callback
  const handleEmailSent = (type, title, message) => {
    showNotification(type, title, message);
    closeEmailComposer();
  };

  // PAN Management Functions
  const maskPAN = (pan) => {
    if (!pan || pan.length < 8) return pan;
    return pan.substring(0, 4) + '****' + pan.substring(pan.length - 4);
  };

  const openPanManagementModal = async (license) => {
    try {
      setPanManagementModal({
        isOpen: true,
        licenseKey: license.license_key,
        licenseData: license,
        boundPANs: license.bound_pans || []
      });
    } catch (error) {
      console.error('Error opening PAN management modal:', error);
      showNotification('error', 'Error', 'Failed to load PAN management data');
    }
  };

  const closePanManagementModal = () => {
    setPanManagementModal({
      isOpen: false,
      licenseKey: '',
      licenseData: null,
      boundPANs: []
    });
  };

  const handleUnbindPAN = async (panHash) => {
    try {
      setActionLoading(prev => ({ ...prev, [`unbind-${panHash}`]: true }));
      
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/pan/unbind`,
        {
          license_key: panManagementModal.licenseKey,
          pan_number: panHash
        },
        {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }
      );

      if (response.data.success) {
        // Update local state
        setPanManagementModal(prev => ({
          ...prev,
          boundPANs: prev.boundPANs.filter(pan => pan !== panHash)
        }));

        // Refresh data
        await fetchData();
        
        showNotification('success', 'PAN Unbound', 'PAN has been successfully unbound from this license');
      }
    } catch (error) {
      console.error('Error unbinding PAN:', error);
      showNotification('error', 'Unbind Failed', error.response?.data?.detail || 'Failed to unbind PAN');
    } finally {
      setActionLoading(prev => ({ ...prev, [`unbind-${panHash}`]: false }));
    }
  };

  const calculateStats = () => {
    const completedOrders = orders.filter(order => order.status === 'completed');
    const totalRevenue = completedOrders.reduce((sum, order) => sum + order.amount, 0) / 100;
    
    const pendingOrders = orders.filter(order => order.status === 'pending').length;
    const totalOrders = orders.length;
    
    // User statistics (excluding admin)
    const nonAdminUsers = users.filter(user => !user.is_admin);
    const totalUsersExcludingAdmin = nonAdminUsers.length;
    
    // License statistics
    const activeLicenses = licenses.filter(license => license.status === 'activated').length;
    const totalLicenses = licenses.length;
    const expiredLicenses = licenses.filter(license => license.status === 'expired').length;
    const betaTrialsUsed = betaTrials.length;
    
    // Overall conversion rate (users who have any completed order)
    const usersWithCompletedOrders = new Set(completedOrders.map(order => order.user_id)).size;
    const totalConversionRate = totalUsersExcludingAdmin > 0 ? 
      Math.round((usersWithCompletedOrders / totalUsersExcludingAdmin) * 100) : 0;
    
    // Beta plan statistics
    const betaOrders = orders.filter(order => order.subscription_type === 'beta');
    const completedBetaOrders = betaOrders.filter(order => order.status === 'completed');
    const betaLicenses = licenses.filter(license => {
      const order = orders.find(o => o.id === license.order_id);
      return order && order.subscription_type === 'beta';
    });
    const activeBetaLicenses = betaLicenses.filter(license => license.status === 'activated').length;
    const expiredBetaLicenses = betaLicenses.filter(license => license.status === 'expired').length;
    
    // Individual plan statistics
    const individualOrders = orders.filter(order => order.subscription_type === 'individual');
    const completedIndividualOrders = individualOrders.filter(order => order.status === 'completed');
    const individualRevenue = completedIndividualOrders.reduce((sum, order) => sum + order.amount, 0) / 100;
    const individualLicenses = licenses.filter(license => {
      const order = orders.find(o => o.id === license.order_id);
      return order && order.subscription_type === 'individual';
    });
    const activeIndividualLicenses = individualLicenses.filter(license => license.status === 'activated').length;
    const expiredIndividualLicenses = individualLicenses.filter(license => license.status === 'expired').length;
    
    // Beta to Individual upgrades (users who had beta and later got individual)
    const betaUsers = new Set(completedBetaOrders.map(order => order.user_id));
    const individualUsers = new Set(completedIndividualOrders.map(order => order.user_id));
    const betaToIndividualUpgrades = [...betaUsers].filter(userId => individualUsers.has(userId)).length;
    const individualConversionRate = betaUsers.size > 0 ? Math.round((betaToIndividualUpgrades / betaUsers.size) * 100) : 0;
    
    // Family plan statistics
    const familyOrders = orders.filter(order => order.subscription_type === 'family');
    const completedFamilyOrders = familyOrders.filter(order => order.status === 'completed');
    const familyRevenue = completedFamilyOrders.reduce((sum, order) => sum + order.amount, 0) / 100;
    const familyLicenses = licenses.filter(license => {
      const order = orders.find(o => o.id === license.order_id);
      return order && order.subscription_type === 'family';
    });
    const activeFamilyLicenses = familyLicenses.filter(license => license.status === 'activated').length;
    
    // Beta to Family upgrades
    const familyUsers = new Set(completedFamilyOrders.map(order => order.user_id));
    const betaToFamilyUpgrades = [...betaUsers].filter(userId => familyUsers.has(userId)).length;
    const familyConversionRate = betaUsers.size > 0 ? Math.round((betaToFamilyUpgrades / betaUsers.size) * 100) : 0;
    
    return {
      // General metrics
      totalRevenue,
      totalUsersExcludingAdmin,
      totalOrders,
      pendingOrders,
      totalConversionRate,
      activeLicenses,
      totalLicenses,
      expiredLicenses,
      
      // Beta plan metrics
      betaOrdersCount: betaOrders.length,
      completedBetaOrdersCount: completedBetaOrders.length,
      activeBetaLicenses,
      expiredBetaLicenses,
      betaTrialsUsed,
      
      // Individual plan metrics  
      individualOrdersCount: individualOrders.length,
      completedIndividualOrdersCount: completedIndividualOrders.length,
      activeIndividualLicenses,
      expiredIndividualLicenses,
      individualRevenue,
      betaToIndividualUpgrades,
      individualConversionRate,
      
      // Family plan metrics
      familyOrdersCount: familyOrders.length,
      completedFamilyOrdersCount: completedFamilyOrders.length,
      activeFamilyLicenses,
      familyRevenue,
      betaToFamilyUpgrades,
      familyConversionRate
    };
  };

  // User Management Functions
  const handleUserAction = async (userId, action) => {
    const userToAction = users.find(u => u.id === userId);
    
    if (action === 'delete') {
      setConfirmAction({
        type: 'deleteUser',
        userId,
        userName: userToAction?.name || 'Unknown',
        userEmail: userToAction?.email || 'Unknown',
        action
      });
      setShowConfirmModal(true);
      return;
    }
    
    // For activate/deactivate actions, show simple confirmation
    setConfirmAction({
      type: 'userAction',
      userId,
      userName: userToAction?.name || 'Unknown',
      userEmail: userToAction?.email || 'Unknown',
      action
    });
    setShowConfirmModal(true);
  };

  const confirmUserAction = async () => {
    if (!confirmAction || (!confirmAction.type.includes('User') && confirmAction.type !== 'userAction')) return;
    
    const { userId, action } = confirmAction;
    setIsDeleting(true);
    
    try {
      const response = await fetch(
        action === 'delete' 
          ? `${process.env.REACT_APP_BACKEND_URL}/api/admin/user/${userId}`
          : `${process.env.REACT_APP_BACKEND_URL}/api/admin/user/${userId}/${action}`,
        {
          method: action === 'delete' ? 'DELETE' : 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      const responseData = await response.json();

      if (response.ok) {
        if (action === 'activate') {
          setUsers(prev => prev.map(user => 
            user.id === userId ? { ...user, is_active: true } : user
          ));
          // Show success modal
          setConfirmAction({
            type: 'success',
            message: `User ${confirmAction.userEmail} has been activated successfully!`
          });
        } else if (action === 'deactivate') {
          setUsers(prev => prev.map(user => 
            user.id === userId ? { ...user, is_active: false } : user
          ));
          // Show success modal
          setConfirmAction({
            type: 'success',
            message: `User ${confirmAction.userEmail} has been deactivated successfully!`
          });
        } else if (action === 'delete') {
          setUsers(prev => prev.filter(user => user.id !== userId));
          // Refresh all data from backend after successful deletion
          await fetchData();
          
          // Build detailed summary with subscription cancellation info
          let detailsText = responseData.summary || '';
          
          // Add subscription cancellation details if available
          if (responseData.subscription_cancellations) {
            const subCancellation = responseData.subscription_cancellations;
            if (subCancellation.total_subscriptions > 0) {
              detailsText += `\n\n📊 Subscription Cancellations:\n`;
              detailsText += `Total: ${subCancellation.total_subscriptions} | `;
              detailsText += `Successfully Cancelled: ${subCancellation.successfully_cancelled}`;
              
              // Show failed cancellations if any
              const failed = subCancellation.total_subscriptions - subCancellation.successfully_cancelled;
              if (failed > 0) {
                detailsText += ` | Failed: ${failed}`;
              }
            }
          }
          
          // Show success modal with detailed summary
          setConfirmAction({
            type: 'success',
            message: `User ${confirmAction.userEmail} and all associated data have been permanently deleted!`,
            details: detailsText
          });
        }
        setShowConfirmModal(true);
      } else {
        throw new Error(responseData.message || `Failed to ${action} user`);
      }
    } catch (error) {
      console.error(`Error ${action} user:`, error);
      
      // Show error modal
      setConfirmAction({
        type: 'error',
        message: `Failed to ${action} user: ${error.message}`,
        userEmail: confirmAction.userEmail
      });
      setShowConfirmModal(true);
    } finally {
      setIsDeleting(false);
    }
  };

  // Handle order deletion
  const handleDeleteOrder = async (orderId) => {
    const orderToDelete = orders.find(o => o.id === orderId);
    const orderUser = users.find(u => u.id === orderToDelete?.user_id);
    
    // Reset cancellation type to default (immediate)
    setSubscriptionCancellationType('immediate');
    
    setConfirmAction({
      type: 'deleteOrder',
      orderId,
      orderDetails: {
        id: formatOrderId(orderId),
        customer: orderUser?.email || 'Unknown',
        plan: orderToDelete?.subscription_type || 'Unknown',
        amount: (orderToDelete?.amount || 0) / 100,
        subscriptionId: orderToDelete?.razorpay_subscription_id,
        subscriptionStatus: orderToDelete?.subscription_status
      }
    });
    setShowConfirmModal(true);
  };

  const confirmDeleteOrder = async () => {
    if (!confirmAction || confirmAction.type !== 'deleteOrder') return;
    
    const { orderId } = confirmAction;
    setIsDeleting(true);
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/orders/${orderId}?cancel_at_cycle_end=${subscriptionCancellationType === 'end_of_cycle' ? '1' : '0'}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      if (response.status === 200) {
        // Refresh all data from backend after successful deletion
        await fetchData();
        
        const deletedSummary = response.data.deleted_summary;
        const subscriptionCancellation = response.data.subscription_cancellation;
        
        // Build summary text
        const summaryText = `Orders: ${deletedSummary.orders}, Licenses: ${deletedSummary.licenses}, Beta Trials: ${deletedSummary.beta_trials}`;
        
        // Build subscription cancellation details
        let subscriptionDetails = '';
        if (subscriptionCancellation?.attempted) {
          if (subscriptionCancellation.cancelled) {
            const cancellationType = subscriptionCancellationType === 'end_of_cycle' 
              ? ' (will remain active until end of billing period)' 
              : ' (cancelled immediately)';
            subscriptionDetails = `\n✅ Razorpay subscription cancelled successfully${cancellationType}`;
          } else if (subscriptionCancellation.error) {
            subscriptionDetails = `\n⚠️ Subscription cancellation: ${subscriptionCancellation.error}`;
          }
        }
        
        // Show success modal
        setConfirmAction({
          type: 'success',
          message: `Order ${confirmAction.orderDetails.id} has been deleted successfully!`,
          details: `Deleted Records: ${summaryText}${subscriptionDetails}`
        });
        setShowConfirmModal(true);
      } else {
        throw new Error(response.data.message || 'Failed to delete order');
      }
    } catch (error) {
      console.error('Error deleting order:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to delete order';
      
      // Show error modal
      setConfirmAction({
        type: 'error',
        message: `Failed to delete order: ${errorMessage}`,
        orderDetails: confirmAction.orderDetails
      });
      setShowConfirmModal(true);
    } finally {
      setIsDeleting(false);
    }
  };

  // Growth Analytics Helper
  const calculateGrowthMetrics = () => {
    const now = new Date();
    const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    const oneMonthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
    const sixMonthsAgo = new Date(now.getTime() - 6 * 30 * 24 * 60 * 60 * 1000);
    const oneYearAgo = new Date(now.getTime() - 365 * 24 * 60 * 60 * 1000);
    
    // Calculate quarter dates
    const currentQuarter = Math.floor((now.getMonth()) / 3);
    const quarterStart = new Date(now.getFullYear(), currentQuarter * 3, 1);
    
    const completedOrders = orders.filter(order => order.status === 'completed');
    
    const getRevenueForPeriod = (startDate) => {
      return completedOrders
        .filter(order => new Date(order.created_at) >= startDate)
        .reduce((sum, order) => sum + order.amount, 0) / 100;
    };
    
    const getOrderCountForPeriod = (startDate) => {
      return completedOrders.filter(order => new Date(order.created_at) >= startDate).length;
    };
    
    return {
      pastWeek: { revenue: getRevenueForPeriod(oneWeekAgo), orders: getOrderCountForPeriod(oneWeekAgo) },
      pastMonth: { revenue: getRevenueForPeriod(oneMonthAgo), orders: getOrderCountForPeriod(oneMonthAgo) },
      past6Months: { revenue: getRevenueForPeriod(sixMonthsAgo), orders: getOrderCountForPeriod(sixMonthsAgo) },
      pastYear: { revenue: getRevenueForPeriod(oneYearAgo), orders: getOrderCountForPeriod(oneYearAgo) },
      currentQuarter: { revenue: getRevenueForPeriod(quarterStart), orders: getOrderCountForPeriod(quarterStart) }
    };
  };

  const stats = calculateStats();
  const growthMetrics = calculateGrowthMetrics();

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleLicenseAction = async (licenseKey, action) => {
    const license = licenses.find(l => l.license_key === licenseKey);
    const licenseUser = users.find(u => u.id === license?.user_id);
    
    setConfirmAction({
      type: 'licenseAction',
      licenseKey,
      action,
      licenseDetails: {
        key: licenseKey,
        customer: licenseUser?.email || 'Unknown',
        status: license?.status || 'Unknown'
      }
    });
    setShowConfirmModal(true);
  };

  const confirmLicenseAction = async () => {
    if (!confirmAction || confirmAction.type !== 'licenseAction') return;
    
    const { licenseKey, action } = confirmAction;
    setIsDeleting(true);
    
    try {
      let url = '';
      switch (action) {
        case 'deactivate':
          url = `/admin/license/${licenseKey}/deactivate`;
          break;
        case 'reactivate':
          url = `/admin/license/${licenseKey}/reactivate`;
          break;
        default:
          throw new Error('Unknown action');
      }
      
      const response = await axios.put(url);
      
      if (response.data.success) {
        // Refresh licenses data
        const licensesResponse = await axios.get('/admin/licenses');
        setLicenses(licensesResponse.data.licenses || []);
        
        // Show success modal
        setConfirmAction({
          type: 'success',
          message: `License ${licenseKey} has been ${action}d successfully!`,
          details: response.data.message
        });
        setShowConfirmModal(true);
      }
    } catch (error) {
      console.error(`Error ${action} license:`, error);
      
      // Show error modal
      setConfirmAction({
        type: 'error',
        message: `Failed to ${action} license: ${error.response?.data?.detail || error.message}`,
        licenseDetails: confirmAction.licenseDetails
      });
      setShowConfirmModal(true);
    } finally {
      setIsDeleting(false);
    }
  };

  // Format Order ID for display
  const formatOrderId = (orderId) => {
    if (!orderId) return 'N/A';
    // Simple formatting for 8-character alphanumeric order IDs
    return `#${orderId}`;
  };

  // Filter functions
  const filteredOrders = orders.filter(order => {
    const orderUser = users.find(user => user.id === order.user_id);
    const orderLicense = licenses.find(license => license.order_id === order.id);
    const formattedOrderId = formatOrderId(order.id);
    const orderDate = new Date(order.created_at).toISOString().split('T')[0]; // Convert to YYYY-MM-DD format
    
    // Safely get filter values with fallbacks
    const orderIdFilter = searchFilters?.orders?.orderId || '';
    const planFilter = searchFilters?.orders?.plan || '';
    const dateFilter = searchFilters?.orders?.date || '';
    const licenseStatusFilter = searchFilters?.orders?.licenseStatus || '';
    const customerFilter = searchFilters?.orders?.customer || '';
    const paymentIdFilter = searchFilters?.orders?.paymentId || '';
    const subscriptionStatusFilter = searchFilters?.orders?.subscriptionStatus || '';
    
    // Subscription status matching
    const matchesSubscriptionStatus = () => {
      if (subscriptionStatusFilter === '') return true;
      if (subscriptionStatusFilter === 'none') {
        return !order.razorpay_subscription_id || order.subscription_type === 'beta';
      }
      return (order.subscription_status || '').toLowerCase() === subscriptionStatusFilter.toLowerCase();
    };
    
    return (
      formattedOrderId.toLowerCase().includes(orderIdFilter.toLowerCase()) &&
      order.subscription_type.includes(planFilter.toLowerCase()) &&
      (dateFilter === '' || orderDate === dateFilter) &&
      (licenseStatusFilter === '' || (orderLicense?.status || 'pending').toLowerCase() === licenseStatusFilter.toLowerCase()) &&
      (orderUser?.email || '').toLowerCase().includes(customerFilter.toLowerCase()) &&
      (order.razorpay_payment_id || '').toLowerCase().includes(paymentIdFilter.toLowerCase()) &&
      matchesSubscriptionStatus()
    );
  });

  const filteredLicenses = licenses.filter(license => {
    const licenseOrder = orders.find(order => order.id === license.order_id);
    const licenseUser = users.find(user => user.id === license.user_id);
    const formattedOrderId = licenseOrder ? formatOrderId(licenseOrder.id) : '';
    
    return (
      license.license_key.toLowerCase().includes(searchFilters.licenses.licenseKey.toLowerCase()) &&
      (searchFilters.licenses.status === '' || license.status.toLowerCase() === searchFilters.licenses.status.toLowerCase()) &&
      formattedOrderId.toLowerCase().includes(searchFilters.licenses.orderId.toLowerCase()) &&
      (licenseUser?.email || '').toLowerCase().includes(searchFilters.licenses.customer.toLowerCase()) &&
      (searchFilters.licenses.planType === '' || (license.subscription_type || 'unknown').toLowerCase() === searchFilters.licenses.planType.toLowerCase())
    );
  });

  const filteredUsers = users.filter(user => {
    const matchesEmail = user.email.toLowerCase().includes(searchFilters.users.email.toLowerCase());
    const matchesRole = (user.is_admin ? 'admin' : 'user').includes(searchFilters.users.role.toLowerCase());
    const matchesEmailVerified = searchFilters.users.emailVerified === '' || 
      (searchFilters.users.emailVerified === 'verified' && user.is_email_verified) ||
      (searchFilters.users.emailVerified === 'unverified' && !user.is_email_verified);
    
    return matchesEmail && matchesRole && matchesEmailVerified;
  });

  const filteredBetaTrials = betaTrials.filter(trial => (
    (trial.email || '').toLowerCase().includes(searchFilters.betaTrials.email.toLowerCase()) &&
    (trial.license_key || '').toLowerCase().includes(searchFilters.betaTrials.licenseKey.toLowerCase())
  ));

  const filteredFeedback = feedback.filter(item => {
    const matchesCustomer = !searchFilters.feedback.customer || 
      item.customer_email.toLowerCase().includes(searchFilters.feedback.customer.toLowerCase()) ||
      item.customer_name.toLowerCase().includes(searchFilters.feedback.customer.toLowerCase());
    
    const matchesRating = !searchFilters.feedback.rating || 
      item.overall_rating.toString() === searchFilters.feedback.rating;
    
    const matchesDate = !searchFilters.feedback.date || 
      new Date(item.created_at).toISOString().split('T')[0] === searchFilters.feedback.date;
    
    return matchesCustomer && matchesRating && matchesDate;
  });

  // CSV Export Functions
  const downloadCSV = (data, filename) => {
    const blob = new Blob([data], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const arrayToCSV = (data, headers) => {
    const csvHeaders = headers.join(',');
    const csvData = data.map(row => 
      headers.map(header => {
        const value = row[header] || '';
        // Escape quotes and wrap in quotes if contains comma
        const stringValue = String(value);
        if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
          return `"${stringValue.replace(/"/g, '""')}"`;
        }
        return stringValue;
      }).join(',')
    ).join('\n');
    
    return `${csvHeaders}\n${csvData}`;
  };

  const exportOrders = () => {
    const headers = ['Order ID', 'Customer Email', 'Plan', 'Amount (₹)', 'Status', 'License Status', 'Payment ID', 'Purchase Date', 'Expiry Date'];
    const exportData = filteredOrders.map(order => {
      const orderUser = users.find(user => user.id === order.user_id);
      const orderLicense = licenses.find(license => license.order_id === order.id);
      const isExpired = order.expires_at && new Date(order.expires_at) < new Date();
      
      return {
        'Order ID': formatOrderId(order.id),
        'Customer Email': orderUser?.email || 'Unknown',
        'Plan': order.subscription_type.charAt(0).toUpperCase() + order.subscription_type.slice(1),
        'Amount (₹)': (order.amount / 100).toLocaleString(),
        'Status': order.status.charAt(0).toUpperCase() + order.status.slice(1),
        'License Status': orderLicense?.status || 'Pending',
        'Payment ID': order.razorpay_payment_id || 'N/A',
        'Purchase Date': new Date(order.created_at).toLocaleDateString(),
        'Expiry Date': order.expires_at 
          ? new Date(order.expires_at).toLocaleDateString()
          : 'N/A'
      };
    });
    
    const csv = arrayToCSV(exportData, headers);
    downloadCSV(csv, `portfolio-auditor-orders-${new Date().toISOString().split('T')[0]}.csv`);
  };

  const exportUsers = () => {
    const headers = ['User ID', 'Email', 'Name', 'Role', 'Status', 'Joined Date', 'Email Verified'];
    const exportData = filteredUsers.map(user => ({
      'User ID': user.id,
      'Email': user.email,
      'Name': user.name,
      'Role': user.is_admin ? 'Admin' : 'User',
      'Status': user.is_active === false ? 'Inactive' : 'Active',
      'Joined Date': new Date(user.created_at).toLocaleDateString(),
      'Email Verified': user.is_email_verified ? 'Yes' : 'No'
    }));
    
    const csv = arrayToCSV(exportData, headers);
    downloadCSV(csv, `portfolio-auditor-users-${new Date().toISOString().split('T')[0]}.csv`);
  };

  const exportFeedbackToCSV = () => {
    const headers = ['Customer Name', 'Customer Email', 'Overall Rating', 'Comments', 'Ease of Use Rating', 'Features Liked', 'Accuracy Rating', 'Support Rating', 'Future Requirements', 'Submitted Date'];
    const exportData = filteredFeedback.map(item => ({
      'Customer Name': item.customer_name || '',
      'Customer Email': item.customer_email || '',
      'Overall Rating': item.overall_rating,
      'Comments': item.comments || '',
      'Ease of Use Rating': item.ease_of_use_rating,
      'Features Liked': item.features_liked || '',
      'Accuracy Rating': item.accuracy_rating,
      'Support Rating': item.support_rating,
      'Future Requirements': item.future_requirements || '',
      'Submitted Date': new Date(item.created_at).toLocaleDateString()
    }));
    
    const csv = arrayToCSV(exportData, headers);
    downloadCSV(csv, `portfolio-auditor-feedback-${new Date().toISOString().split('T')[0]}.csv`);
  };

  // File delete handler
  const confirmDeleteFile = async () => {
    if (!confirmAction || confirmAction.type !== 'deleteFile') return;
    
    setIsDeleting(true);
    try {
      await confirmAction.action();
      setShowConfirmModal(false);
      setConfirmAction(null);
    } catch (error) {
      console.error('Delete file failed:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  // Feedback delete handlers
  const handleDeleteFeedback = (feedbackId, customerName, customerEmail) => {
    setConfirmAction({
      type: 'deleteFeedback',
      feedbackId,
      customerName,
      customerEmail
    });
    setShowConfirmModal(true);
  };

  const confirmDeleteFeedback = async () => {
    if (!confirmAction || confirmAction.type !== 'deleteFeedback') return;
    
    setIsDeleting(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/feedback/${confirmAction.feedbackId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        // Remove feedback from local state
        setFeedback(prev => prev.filter(item => item.id !== confirmAction.feedbackId));
        setShowConfirmModal(false);
        setConfirmAction(null);
        
        // Show success notification using custom modal
        setConfirmAction({
          type: 'success',
          message: `Feedback from ${confirmAction.customerName} has been deleted successfully!`
        });
        setShowConfirmModal(true);
      } else {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to delete feedback');
      }
    } catch (error) {
      console.error('Error deleting feedback:', error);
      
      // Show error using custom modal instead of alert
      setConfirmAction({
        type: 'error',
        message: `Failed to delete feedback: ${error.message}`,
        customerName: confirmAction.customerName,
        customerEmail: confirmAction.customerEmail
      });
      setShowConfirmModal(true);
    } finally {
      setIsDeleting(false);
    }
  };

  const exportLicenses = () => {
    const headers = ['License Key', 'Customer Email', 'Order ID', 'Status', 'Plan Type', 'Hardware ID', 'PAN Limit', 'Bound PANs Count', 'Bound PANs (Masked)', 'Activation Date', 'Expiration Date'];
    const exportData = filteredLicenses.map(license => {
      const licenseUser = users.find(user => user.id === license.user_id);
      const licenseOrder = orders.find(order => order.id === license.order_id);
      const boundPANs = license.bound_pans || [];
      
      return {
        'License Key': license.license_key,
        'Customer Email': licenseUser?.email || 'Unknown',
        'Order ID': licenseOrder ? formatOrderId(licenseOrder.id) : 'Unknown',
        'Status': license.status.charAt(0).toUpperCase() + license.status.slice(1),
        'Plan Type': (license.subscription_type || 'Unknown').charAt(0).toUpperCase() + (license.subscription_type || 'Unknown').slice(1),
        'Hardware ID': license.hardware_id || 'Not Activated',
        'PAN Limit': license.pan_limit || 1,
        'Bound PANs Count': boundPANs.length,
        'Bound PANs (Masked)': boundPANs.length > 0 ? boundPANs.map(pan => maskPAN(pan)).join(', ') : 'None',
        'Activation Date': license.activation_date 
          ? new Date(license.activation_date).toLocaleDateString()
          : 'Not Activated',
        'Expiration Date': license.expiration_date
          ? new Date(license.expiration_date).toLocaleDateString()
          : 'Not Set'
      };
    });
    
    const csv = arrayToCSV(exportData, headers);
    downloadCSV(csv, `portfolio-auditor-licenses-${new Date().toISOString().split('T')[0]}.csv`);
  };

  const exportBetaTrials = () => {
    const headers = ['Customer Email', 'License Key', 'Hardware ID', 'Activation Date'];
    const exportData = filteredBetaTrials.map(trial => ({
      'Customer Email': trial.email,
      'License Key': trial.license_key,
      'Hardware ID': trial.hardware_id,
      'Activation Date': new Date(trial.activation_date).toLocaleDateString()
    }));
    
    const csv = arrayToCSV(exportData, headers);
    downloadCSV(csv, `portfolio-auditor-beta-trials-${new Date().toISOString().split('T')[0]}.csv`);
  };

  const exportTopCustomers = () => {
    const headers = ['Customer Name', 'Email', 'Total Orders', 'Completed Orders', 'Success Rate (%)', 'Total Revenue (₹)', 'Earned Revenue (₹)', 'Avg Per Order (₹)', 'Customer Since (Days)'];
    const exportData = filteredTopCustomers.map(customer => ({
      'Customer Name': customer.user.name,
      'Email': customer.user.email,
      'Total Orders': customer.totalOrders,
      'Completed Orders': customer.completedOrders,
      'Success Rate (%)': customer.totalOrders > 0 ? Math.round((customer.completedOrders / customer.totalOrders) * 100) : 0,
      'Total Revenue (₹)': (customer.totalRevenue / 100).toLocaleString(),
      'Earned Revenue (₹)': (customer.completedRevenue / 100).toLocaleString(),
      'Avg Per Order (₹)': customer.completedOrders > 0 ? Math.round((customer.completedRevenue / customer.completedOrders) / 100).toLocaleString() : 0,
      'Customer Since (Days)': Math.ceil((Date.now() - customer.firstOrderDate) / (1000 * 60 * 60 * 24))
    }));
    
    const csv = arrayToCSV(exportData, headers);
    downloadCSV(csv, `portfolio-auditor-top-customers-${new Date().toISOString().split('T')[0]}.csv`);
  };

  // Pagination Functions
  const getPaginatedData = (data, tabName) => {
    const currentPage = pagination[tabName].currentPage;
    const itemsPerPage = pagination[tabName].itemsPerPage;
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return data.slice(startIndex, endIndex);
  };

  const getTotalPages = (data, tabName) => {
    return Math.ceil(data.length / pagination[tabName].itemsPerPage);
  };

  const handlePageChange = (tabName, newPage) => {
    setPagination(prev => ({
      ...prev,
      [tabName]: { ...prev[tabName], currentPage: newPage }
    }));
  };

  const PaginationControls = ({ data, tabName }) => {
    const totalPages = getTotalPages(data, tabName);
    const currentPage = pagination[tabName].currentPage;
    
    if (totalPages <= 1) return null;
    
    return (
      <div className="flex items-center justify-between px-4 py-3 bg-white border-t border-slate-200 sm:px-6">
        <div className="flex items-center justify-between w-full">
          <div className="flex items-center text-sm text-slate-700">
            <span>
              Showing {Math.min((currentPage - 1) * pagination[tabName].itemsPerPage + 1, data.length)} to{' '}
              {Math.min(currentPage * pagination[tabName].itemsPerPage, data.length)} of {data.length} results
            </span>
          </div>
          
          <div className="flex items-center space-x-2">
            <button
              onClick={() => handlePageChange(tabName, currentPage - 1)}
              disabled={currentPage === 1}
              className="px-3 py-1 border border-slate-300 rounded-md text-sm font-medium text-slate-500 bg-white hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            
            {/* Page Numbers */}
            <div className="flex items-center space-x-1">
              {[...Array(totalPages)].map((_, index) => {
                const page = index + 1;
                if (totalPages > 7 && page > 3 && page < totalPages - 2 && Math.abs(page - currentPage) > 1) {
                  if (page === 4 || page === totalPages - 3) {
                    return <span key={page} className="px-2 py-1 text-slate-400">...</span>;
                  }
                  return null;
                }
                
                return (
                  <button
                    key={page}
                    onClick={() => handlePageChange(tabName, page)}
                    className={`px-3 py-1 border rounded-md text-sm font-medium ${
                      currentPage === page
                        ? 'border-blue-500 bg-blue-50 text-blue-600'
                        : 'border-slate-300 text-slate-500 bg-white hover:bg-slate-50'
                    }`}
                  >
                    {page}
                  </button>
                );
              })}
            </div>
            
            <button
              onClick={() => handlePageChange(tabName, currentPage + 1)}
              disabled={currentPage === totalPages}
              className="px-3 py-1 border border-slate-300 rounded-md text-sm font-medium text-slate-500 bg-white hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Calculate top customers with more than 1 order
  const getTopCustomers = () => {
    // Group orders by user_id
    const customerOrderMap = {};
    
    orders.forEach(order => {
      if (!customerOrderMap[order.user_id]) {
        customerOrderMap[order.user_id] = {
          user_id: order.user_id,
          orders: [],
          totalOrders: 0,
          totalRevenue: 0,
          completedOrders: 0,
          completedRevenue: 0
        };
      }
      
      customerOrderMap[order.user_id].orders.push(order);
      customerOrderMap[order.user_id].totalOrders++;
      customerOrderMap[order.user_id].totalRevenue += order.amount;
      
      if (order.status === 'completed') {
        customerOrderMap[order.user_id].completedOrders++;
        customerOrderMap[order.user_id].completedRevenue += order.amount;
      }
    });
    
    // Filter customers with more than 1 order and add user details
    const topCustomers = Object.values(customerOrderMap)
      .filter(customer => customer.totalOrders > 1)
      .map(customer => {
        const user = users.find(u => u.id === customer.user_id);
        return {
          ...customer,
          user: user || { email: 'Unknown User', name: 'Unknown' },
          lastOrderDate: Math.max(...customer.orders.map(o => new Date(o.created_at).getTime())),
          firstOrderDate: Math.min(...customer.orders.map(o => new Date(o.created_at).getTime()))
        };
      })
      .sort((a, b) => b.totalOrders - a.totalOrders) // Sort by total orders descending
      .slice(0, 20); // Get top 20
    
    return topCustomers;
  };

  const topCustomers = getTopCustomers();
  
  const filteredTopCustomers = topCustomers.filter(customer => 
    customer.user.email.toLowerCase().includes(searchFilters.topCustomers.email.toLowerCase())
  );

  // Get paginated data for each tab (must be after all filtered data definitions)
  const paginatedOrders = getPaginatedData(filteredOrders, 'orders');
  const paginatedUsers = getPaginatedData(filteredUsers, 'users');
  const paginatedLicenses = getPaginatedData(filteredLicenses, 'licenses');
  const paginatedBetaTrials = getPaginatedData(filteredBetaTrials, 'betaTrials');
  const paginatedTopCustomers = getPaginatedData(filteredTopCustomers, 'topCustomers');
  const paginatedFeedback = getPaginatedData(filteredFeedback, 'feedback');

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString() + ' ' + new Date(dateString).toLocaleTimeString();
    } catch {
      return 'Invalid Date';
    }
  };

  const isLicenseExpired = (expirationDate) => {
    if (!expirationDate) return false;
    return new Date(expirationDate) < new Date();
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/dashboard')}
                className="bg-slate-100 text-slate-700 px-3 py-2 rounded-lg hover:bg-slate-200 transition-colors flex items-center space-x-2"
                data-testid="back-to-dashboard"
              >
                <ArrowLeft className="w-4 h-4" />
                <span>Dashboard</span>
              </button>
              <div className="flex items-center space-x-2">
                <div className="w-10 h-10 bg-gradient-to-r from-purple-600 to-purple-700 rounded-xl flex items-center justify-center">
                  <BarChart3 className="w-6 h-6 text-white" />
                </div>
                <span className="text-2xl font-bold text-slate-800">Admin Panel</span>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <div className="text-right">
                <p className="text-sm text-slate-600">Admin</p>
                <p className="font-semibold text-slate-900" data-testid="admin-name">{user?.name}</p>
              </div>
              <button
                onClick={async () => {
                  setLoading(true);
                  await fetchData();
                  setLoading(false);
                  showNotification('success', 'Data Refreshed', 'All admin panel data has been updated successfully.');
                }}
                disabled={loading}
                className={`inline-flex items-center space-x-2 px-4 py-2 rounded-lg font-medium transition-all ${
                  loading 
                    ? 'bg-slate-100 text-slate-400 cursor-not-allowed' 
                    : 'bg-green-600 hover:bg-green-700 text-white hover:scale-105'
                }`}
                title="Refresh all data"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                <span>{loading ? 'Refreshing...' : 'Refresh All'}</span>
              </button>
              <button
                onClick={() => setBulkEmailModalOpen(true)}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
                title="Send bulk email to customers"
              >
                <Mail className="w-4 h-4" />
                <span>Bulk Email</span>
              </button>
              <button
                onClick={handleLogout}
                className="bg-slate-100 text-slate-700 px-4 py-2 rounded-lg hover:bg-slate-200 transition-colors flex items-center space-x-2"
                data-testid="admin-logout-btn"
              >
                <LogOut className="w-4 h-4" />
                <span>Logout</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Key Metrics Overview */}
        <div className="grid md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-xl p-6 border border-slate-200">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-slate-600">Total Revenue</p>
                <p className="text-2xl font-bold text-slate-900" data-testid="total-revenue">
                  ₹{stats.totalRevenue.toLocaleString()}
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl p-6 border border-slate-200">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-slate-600">Total Users</p>
                <p className="text-2xl font-bold text-slate-900" data-testid="total-users">
                  {stats.totalUsersExcludingAdmin}
                </p>
                <p className="text-xs text-slate-500">Excluding admins</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl p-6 border border-slate-200">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                <ShoppingCart className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-slate-600">Total Orders</p>
                <p className="text-2xl font-bold text-slate-900" data-testid="total-orders">
                  {stats.totalOrders}
                </p>
                <p className="text-xs text-slate-500">{stats.pendingOrders} pending</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl p-6 border border-slate-200">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-slate-600">Conversion Rate</p>
                <p className="text-2xl font-bold text-slate-900" data-testid="signup-conversion-rate">
                  {stats.totalConversionRate}%
                </p>
                <p className="text-xs text-slate-500">Signup to purchase</p>
              </div>
            </div>
          </div>
        </div>

        {/* License Overview */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-xl p-6 border border-slate-200">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-indigo-100 rounded-xl flex items-center justify-center">
                <Key className="w-6 h-6 text-indigo-600" />
              </div>
              <div>
                <p className="text-sm text-slate-600">Active Licenses</p>
                <p className="text-2xl font-bold text-slate-900" data-testid="active-licenses">
                  {stats.activeLicenses}
                </p>
                <p className="text-xs text-slate-500">{stats.totalLicenses} total issued</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-6 border border-slate-200">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
                <XCircle className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <p className="text-sm text-slate-600">Expired Licenses</p>
                <p className="text-2xl font-bold text-slate-900" data-testid="expired-licenses">
                  {stats.expiredLicenses}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-6 border border-slate-200">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
                <Shield className="w-6 h-6 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-slate-600">Beta Trials Used</p>
                <p className="text-2xl font-bold text-slate-900" data-testid="beta-trials-used">
                  {stats.betaTrialsUsed}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* PAN Usage Overview */}
        <div className="grid md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-xl p-6 border border-slate-200">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-cyan-100 rounded-xl flex items-center justify-center">
                <Settings className="w-6 h-6 text-cyan-600" />
              </div>
              <div>
                <p className="text-sm text-slate-600">PAN Licenses</p>
                <p className="text-2xl font-bold text-slate-900" data-testid="pan-licenses">
                  {panUsageStats.licensesWithPANs || 0}
                </p>
                <p className="text-xs text-slate-500">of {panUsageStats.totalLicenses || 0} licenses</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl p-6 border border-slate-200">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                <CreditCard className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-slate-600">Bound PANs</p>
                <p className="text-2xl font-bold text-slate-900" data-testid="bound-pans">
                  {panUsageStats.totalBoundPANs || 0}
                </p>
                <p className="text-xs text-slate-500">across all licenses</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl p-6 border border-slate-200">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-slate-600">PAN Utilization</p>
                <p className="text-2xl font-bold text-slate-900" data-testid="pan-utilization">
                  {panUsageStats.panUtilization || 0}%
                </p>
                <p className="text-xs text-slate-500">PAN capacity used</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl p-6 border border-slate-200">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                <Shield className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-slate-600">Family Plans</p>
                <p className="text-2xl font-bold text-slate-900" data-testid="family-licenses">
                  {licenses.filter(l => l.subscription_type === 'family').length}
                </p>
                <p className="text-xs text-slate-500">{process.env.REACT_APP_FAMILY_PAN_LIMIT || '3'} PANs each</p>
              </div>
            </div>
          </div>
        </div>

        {/* Growth Analytics */}
        <div className="mb-8">
          <h4 className="text-lg font-semibold text-slate-900 mb-4">Revenue Growth Analytics</h4>
          <div className="grid md:grid-cols-5 gap-4">
            <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-4 border border-green-200">
              <h5 className="text-sm font-semibold text-slate-700 mb-2">Past Week</h5>
              <p className="text-xl font-bold text-green-600">₹{growthMetrics.pastWeek.revenue.toLocaleString()}</p>
              <p className="text-xs text-slate-500">{growthMetrics.pastWeek.orders} orders</p>
            </div>
            <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl p-4 border border-blue-200">
              <h5 className="text-sm font-semibold text-slate-700 mb-2">Past Month</h5>
              <p className="text-xl font-bold text-blue-600">₹{growthMetrics.pastMonth.revenue.toLocaleString()}</p>
              <p className="text-xs text-slate-500">{growthMetrics.pastMonth.orders} orders</p>
            </div>
            <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-xl p-4 border border-purple-200">
              <h5 className="text-sm font-semibold text-slate-700 mb-2">Past 6 Months</h5>
              <p className="text-xl font-bold text-purple-600">₹{growthMetrics.past6Months.revenue.toLocaleString()}</p>
              <p className="text-xs text-slate-500">{growthMetrics.past6Months.orders} orders</p>
            </div>
            <div className="bg-gradient-to-br from-orange-50 to-red-50 rounded-xl p-4 border border-orange-200">
              <h5 className="text-sm font-semibold text-slate-700 mb-2">Past Year</h5>
              <p className="text-xl font-bold text-orange-600">₹{growthMetrics.pastYear.revenue.toLocaleString()}</p>
              <p className="text-xs text-slate-500">{growthMetrics.pastYear.orders} orders</p>
            </div>
            <div className="bg-gradient-to-br from-teal-50 to-green-50 rounded-xl p-4 border border-teal-200">
              <h5 className="text-sm font-semibold text-slate-700 mb-2">Current Quarter</h5>
              <p className="text-xl font-bold text-teal-600">₹{growthMetrics.currentQuarter.revenue.toLocaleString()}</p>
              <p className="text-xs text-slate-500">{growthMetrics.currentQuarter.orders} orders</p>
            </div>
          </div>
        </div>

        {/* Plan-Specific Analytics */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          {/* Beta Plan Stats */}
          <div className="bg-gradient-to-br from-orange-50 to-yellow-50 rounded-xl p-6 border border-orange-200">
            <h4 className="text-lg font-semibold text-slate-900 mb-4 flex items-center">
              <Shield className="w-5 h-5 text-orange-600 mr-2" />
              Beta Plan Analytics
            </h4>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-sm text-slate-600">Total Orders:</span>
                <span className="font-semibold">{stats.betaOrdersCount}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-slate-600">Completed:</span>
                <span className="font-semibold">{stats.completedBetaOrdersCount}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-slate-600">Active Licenses:</span>
                <span className="font-semibold text-green-600">{stats.activeBetaLicenses}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-slate-600">Expired:</span>
                <span className="font-semibold text-red-600">{stats.expiredBetaLicenses}</span>
              </div>
            </div>
          </div>

          {/* Individual Plan Stats */}
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-200">
            <h4 className="text-lg font-semibold text-slate-900 mb-4 flex items-center">
              <Calendar className="w-5 h-5 text-blue-600 mr-2" />
              Individual Plan Analytics
            </h4>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-sm text-slate-600">Total Orders:</span>
                <span className="font-semibold">{stats.individualOrdersCount}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-slate-600">Active Licenses:</span>
                <span className="font-semibold text-green-600">{stats.activeIndividualLicenses}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-slate-600">Expired:</span>
                <span className="font-semibold text-red-600">{stats.expiredIndividualLicenses}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-slate-600">Revenue:</span>
                <span className="font-semibold text-blue-600">₹{stats.individualRevenue.toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-slate-600">Beta Upgrades:</span>
                <span className="font-semibold">{stats.betaToIndividualUpgrades}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-slate-600">Conversion:</span>
                <span className="font-semibold text-emerald-600">{stats.individualConversionRate}%</span>
              </div>
            </div>
          </div>

          {/* Family Plan Stats */}
          <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-6 border border-purple-200">
            <h4 className="text-lg font-semibold text-slate-900 mb-4 flex items-center">
              <TrendingUp className="w-5 h-5 text-purple-600 mr-2" />
              Family Plan Analytics
            </h4>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-sm text-slate-600">Total Orders:</span>
                <span className="font-semibold">{stats.familyOrdersCount}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-slate-600">Active Licenses:</span>
                <span className="font-semibold text-green-600">{stats.activeFamilyLicenses}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-slate-600">Revenue:</span>
                <span className="font-semibold text-purple-600">₹{stats.familyRevenue.toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-slate-600">Beta Upgrades:</span>
                <span className="font-semibold">{stats.betaToFamilyUpgrades}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-slate-600">Conversion:</span>
                <span className="font-semibold text-emerald-600">{stats.familyConversionRate}%</span>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-2xl border border-slate-200 mb-8">
          <div className="border-b border-slate-200">
            <nav className="flex space-x-8 px-6">
              {[
                { id: 'overview', name: 'Overview', icon: BarChart3 },
                { id: 'orders', name: 'Orders', icon: ShoppingCart },
                { id: 'top-customers', name: 'Top Customers', icon: TrendingUp },
                { id: 'licenses', name: 'Licenses', icon: Key },
                { id: 'beta-trials', name: 'Beta Trials', icon: Shield },
                { id: 'software-management', name: 'Software Management', icon: Download },
                { id: 'feedback', name: 'Feedback', icon: MessageSquare },
                { id: 'users', name: 'Users', icon: Users }
              ].map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center space-x-2 py-4 px-2 border-b-2 font-medium text-sm transition-colors ${
                      activeTab === tab.id
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
                    }`}
                    data-testid={`tab-${tab.id}`}
                  >
                    <Icon className="w-4 h-4" />
                    <span>{tab.name}</span>
                  </button>
                );
              })}
            </nav>
          </div>

          <div className="p-6">
            {activeTab === 'overview' && (
              <div>
                <h3 className="text-xl font-bold text-slate-900 mb-4">Recent Activity</h3>
                <div className="space-y-3">
                  {orders.slice(0, 5).map((order) => {
                    const orderUser = users.find(u => u.id === order.user_id);
                    const orderLicense = licenses.find(l => l.order_id === order.id);
                    
                    // Generate meaningful activity description
                    const getActivityDescription = () => {
                      if (order.status === 'completed') {
                        if (order.subscription_type === 'beta') {
                          return `New beta trial activated by ${orderUser?.email || 'Unknown user'}`;
                        } else {
                          return `${order.subscription_type.charAt(0).toUpperCase() + order.subscription_type.slice(1)} plan purchased by ${orderUser?.email || 'Unknown user'}`;
                        }
                      } else if (order.status === 'pending') {
                        return `${order.subscription_type.charAt(0).toUpperCase() + order.subscription_type.slice(1)} order created by ${orderUser?.email || 'Unknown user'} - Payment pending`;
                      } else {
                        return `Order ${order.status} for ${orderUser?.email || 'Unknown user'}`;
                      }
                    };

                    const getActivityIcon = () => {
                      if (order.status === 'completed') {
                        if (order.subscription_type === 'beta') {
                          return <Users className="w-5 h-5 text-green-600" />;
                        } else {
                          return <ShoppingCart className="w-5 h-5 text-blue-600" />;
                        }
                      } else if (order.status === 'pending') {
                        return <Clock className="w-5 h-5 text-orange-500" />;
                      } else {
                        return <AlertCircle className="w-5 h-5 text-red-500" />;
                      }
                    };

                    const getTimeAgo = () => {
                      const now = new Date();
                      const orderDate = new Date(order.created_at);
                      const diffInMinutes = Math.floor((now - orderDate) / (1000 * 60));
                      
                      if (diffInMinutes < 1) return 'Just now';
                      if (diffInMinutes < 60) return `${diffInMinutes} minutes ago`;
                      
                      const diffInHours = Math.floor(diffInMinutes / 60);
                      if (diffInHours < 24) return `${diffInHours} hours ago`;
                      
                      const diffInDays = Math.floor(diffInHours / 24);
                      if (diffInDays < 7) return `${diffInDays} days ago`;
                      
                      return orderDate.toLocaleDateString();
                    };

                    return (
                      <div key={order.id} className="flex items-start space-x-3 p-4 bg-white border border-slate-200 rounded-lg hover:shadow-sm transition-shadow">
                        <div className="flex-shrink-0 mt-0.5">
                          {getActivityIcon()}
                        </div>
                        <div className="flex-grow min-w-0">
                          <p className="text-sm font-medium text-slate-900 leading-5">
                            {getActivityDescription()}
                          </p>
                          <div className="mt-1 flex items-center space-x-4 text-xs text-slate-500">
                            <span>Order ID: #{formatOrderId(order.id)}</span>
                            {order.amount > 0 && (
                              <span>Amount: ₹{(order.amount / 100).toLocaleString()}</span>
                            )}
                            {orderLicense && (
                              <span className="flex items-center">
                                <Key className="w-3 h-3 mr-1" />
                                License {orderLicense.status}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex-shrink-0 text-right">
                          <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${getStatusClass(order.status)}`}>
                            {order.status}
                          </span>
                          <p className="text-xs text-slate-500 mt-1">
                            {getTimeAgo()}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                  
                  {orders.length === 0 && (
                    <div className="text-center py-8 text-slate-500">
                      <Users className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                      <p>No recent activity to display</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'orders' && (
              <div>
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-xl font-bold text-slate-900">All Orders</h3>
                  <div className="flex items-center space-x-4">
                    <button
                      onClick={exportOrders}
                      className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Export CSV
                    </button>
                    <div className="text-sm text-slate-600 bg-slate-50 px-4 py-2 rounded-lg">
                      Showing {paginatedOrders.length} of {filteredOrders.length} filtered orders ({orders.length} total)
                    </div>
                  </div>
                </div>
                
                {/* Search Filters */}
                <div className="bg-slate-50 rounded-lg p-4 mb-6">
                  <h4 className="text-sm font-semibold text-slate-700 mb-3">Search & Filter Orders</h4>
                  <div className="grid md:grid-cols-6 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">Order ID</label>
                      <input
                        type="text"
                        placeholder="e.g. #16DF3940"
                        className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        value={searchFilters.orders.orderId}
                        onChange={(e) => setSearchFilters(prev => ({
                          ...prev,
                          orders: { ...prev.orders, orderId: e.target.value }
                        }))}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">Customer Email</label>
                      <input
                        type="text"
                        placeholder="Search by email..."
                        className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        value={searchFilters.orders.customer}
                        onChange={(e) => setSearchFilters(prev => ({
                          ...prev,
                          orders: { ...prev.orders, customer: e.target.value }
                        }))}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">Payment ID</label>
                      <input
                        type="text"
                        placeholder="e.g. pay_xyz123..."
                        className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        value={searchFilters.orders.paymentId}
                        onChange={(e) => setSearchFilters(prev => ({
                          ...prev,
                          orders: { ...prev.orders, paymentId: e.target.value }
                        }))}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">Plan Type</label>
                      <select
                        className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        value={searchFilters.orders.plan}
                        onChange={(e) => setSearchFilters(prev => ({
                          ...prev,
                          orders: { ...prev.orders, plan: e.target.value }
                        }))}
                      >
                        <option value="">All Plans</option>
                        <option value="beta">Beta</option>
                        <option value="individual">Individual</option>
                        <option value="family">Family</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">License Status</label>
                      <select
                        className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        value={searchFilters.orders.licenseStatus}
                        onChange={(e) => setSearchFilters(prev => ({
                          ...prev,
                          orders: { ...prev.orders, licenseStatus: e.target.value }
                        }))}
                      >
                        <option value="">All Statuses</option>
                        <option value="activated">Activated</option>
                        <option value="unactivated">Unactivated</option>
                        <option value="expired">Expired</option>
                        <option value="deactivated">Deactivated</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">Order Date</label>
                      <input
                        type="date"
                        className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        value={searchFilters.orders.date}
                        onChange={(e) => setSearchFilters(prev => ({
                          ...prev,
                          orders: { ...prev.orders, date: e.target.value }
                        }))}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">Subscription Status</label>
                      <select
                        className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        value={searchFilters.orders.subscriptionStatus}
                        onChange={(e) => setSearchFilters(prev => ({
                          ...prev,
                          orders: { ...prev.orders, subscriptionStatus: e.target.value }
                        }))}
                      >
                        <option value="">All Statuses</option>
                        <option value="active">Active</option>
                        <option value="pending">Pending</option>
                        <option value="created">Created</option>
                        <option value="cancelled">Cancelled</option>
                        <option value="expired">Expired</option>
                        <option value="completed">Completed</option>
                        <option value="halted">Halted</option>
                        <option value="none">No Subscription (Beta)</option>
                      </select>
                    </div>
                  </div>
                  <button
                    onClick={() => setSearchFilters(prev => ({
                      ...prev,
                      orders: { orderId: '', date: '', plan: '', licenseStatus: '', customer: '', paymentId: '', subscriptionStatus: '' }
                    }))}
                    className="mt-3 px-4 py-2 bg-slate-600 text-white rounded-md text-sm hover:bg-slate-700 transition-colors"
                  >
                    Clear Filters
                  </button>
                </div>
                
                {/* Card Layout for Better Mobile Experience */}
                <div className="grid gap-6 md:hidden">
                  {paginatedOrders.map((order) => {
                    const orderLicense = licenses.find(license => license.order_id === order.id);
                    const orderUser = users.find(user => user.id === order.user_id);
                    return (
                      <div key={order.id} className="bg-white border border-slate-200 rounded-lg p-4 shadow-sm">
                        <div className="space-y-3 text-sm">
                          <div>
                            <span className="font-medium text-slate-700">Order ID:</span>
                            <p className="font-mono text-slate-900 text-lg font-bold">{formatOrderId(order.id)}</p>
                          </div>
                          <div>
                            <span className="font-medium text-slate-700">Customer Email:</span>
                            <p className="text-slate-900 break-all">{orderUser?.email || 'Unknown User'}</p>
                          </div>
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <span className="font-medium text-slate-700">Plan:</span>
                              <p className="capitalize font-semibold">{order.subscription_type}</p>
                            </div>
                            <div>
                              <span className="font-medium text-slate-700">Amount:</span>
                              <p className="font-semibold text-green-600">₹{order.amount / 100}</p>
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <span className="font-medium text-slate-700">Status:</span>
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusClass(order.status)}`}>
                                {order.status}
                              </span>
                            </div>
                            <div>
                              <span className="font-medium text-slate-700">Date:</span>
                              <p>{new Date(order.created_at).toLocaleDateString()}</p>
                            </div>
                          </div>
                        </div>
                        
                        {/* License Information */}
                        <div className="mt-4 pt-3 border-t border-slate-200">
                          <div className="space-y-2 text-sm">
                            <div>
                              <span className="font-medium text-slate-700">License Key:</span>
                              <p className="font-mono text-slate-600 break-all text-xs">
                                {order.license_key || (order.status === 'completed' ? 'Missing License Key' : 'Not Generated Yet')}
                              </p>
                            </div>
                            {order.razorpay_payment_id && (
                              <div>
                                <span className="font-medium text-slate-700">Payment ID:</span>
                                <div className="flex items-center space-x-2 mt-1">
                                  <p className="font-mono text-blue-700 bg-blue-50 px-2 py-1 rounded text-xs break-all flex-1">
                                    {order.razorpay_payment_id}
                                  </p>
                                  <button
                                    onClick={() => navigator.clipboard.writeText(order.razorpay_payment_id)}
                                    className="text-slate-400 hover:text-slate-600 transition-colors"
                                    title="Copy Payment ID"
                                  >
                                    <CreditCard className="w-4 h-4" />
                                  </button>
                                </div>
                              </div>
                            )}
                            <div>
                              <span className="font-medium text-slate-700">License Status:</span>
                              {orderLicense ? (
                                <div className="flex items-center space-x-2 mt-1">
                                  {getLicenseStatusIcon(orderLicense.status)}
                                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getLicenseStatusClass(orderLicense.status)}`}>
                                    {orderLicense.status}
                                  </span>
                                </div>
                              ) : (
                                <p className={order.status === 'completed' ? 'text-red-600' : 'text-gray-400'}>
                                  {order.status === 'completed' ? 'No license found' : 'Pending'}
                                </p>
                              )}
                            </div>
                          </div>
                          
                          {/* Actions Section */}
                          <div className="pt-3 border-t border-slate-200">
                            <div className="flex flex-col space-y-2">
                              <button
                                onClick={() => openEmailComposer(order, orderUser)}
                                className="inline-flex items-center px-3 py-2 border border-slate-300 shadow-sm text-xs font-medium rounded-md text-slate-700 bg-white hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 w-full justify-center"
                                data-testid={`email-customer-mobile-${order.id}`}
                                title={`Send email to ${orderUser?.email || 'customer'}`}
                              >
                                <Mail className="w-4 h-4 mr-2" />
                                Email Customer
                              </button>
                              <button
                                onClick={() => handleDeleteOrder(order.id)}
                                disabled={actionLoading[`delete-${order.id}`]}
                                className="inline-flex items-center px-3 py-2 border border-red-300 shadow-sm text-xs font-medium rounded-md text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 w-full justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                                data-testid={`delete-order-mobile-${order.id}`}
                                title={`Delete order ${formatOrderId(order.id)}`}
                              >
                                {actionLoading[`delete-${order.id}`] ? (
                                  <>
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600 mr-2"></div>
                                    Deleting...
                                  </>
                                ) : (
                                  <>
                                    <Trash2 className="w-4 h-4 mr-2" />
                                    Delete Order
                                  </>
                                )}
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Enhanced Table Layout for Desktop */}
                <div className="hidden md:block">
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-slate-200 table-fixed">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider" style={{minWidth: '200px', width: '200px'}}>
                            Order & Customer
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider" style={{minWidth: '280px', width: '280px'}}>
                            Plan & Payment
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider" style={{minWidth: '260px', width: '260px'}}>
                            License Details
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider" style={{minWidth: '200px', width: '200px'}}>
                            Subscription Info
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider" style={{minWidth: '160px', width: '160px'}}>
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-slate-200">
                        {paginatedOrders.map((order) => {
                          const orderLicense = licenses.find(license => license.order_id === order.id);
                          const orderUser = users.find(user => user.id === order.user_id);
                          return (
                            <tr key={order.id} data-testid={`admin-order-${order.id}`}>
                              {/* Order & Customer Column */}
                              <td className="px-4 py-4 text-sm" style={{minWidth: '200px', width: '200px'}}>
                                <div>
                                  <p className="font-semibold text-slate-900 text-xs mb-1">Order ID:</p>
                                  <p className="font-mono text-slate-900 font-bold text-sm mb-2">{formatOrderId(order.id)}</p>
                                  <p className="font-semibold text-slate-900 text-xs mb-1">Customer:</p>
                                  <p className="text-slate-600 text-xs break-all mb-2">{orderUser?.email || 'Unknown User'}</p>
                                  <p className="text-slate-400 text-xs">{new Date(order.created_at).toLocaleDateString()}</p>
                                </div>
                              </td>
                              
                              {/* Plan & Payment Column */}
                              <td className="px-4 py-4 text-sm" style={{minWidth: '280px', width: '280px'}}>
                                <div>
                                  <div className="flex items-center justify-between mb-2">
                                    <span className="text-xs font-semibold text-slate-500">PLAN</span>
                                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                      order.subscription_type === 'beta' ? 'bg-orange-100 text-orange-800' :
                                      order.subscription_type === 'individual' ? 'bg-purple-100 text-purple-800' :
                                      'bg-green-100 text-green-800'
                                    }`}>
                                      {order.subscription_type.charAt(0).toUpperCase() + order.subscription_type.slice(1)}
                                    </span>
                                  </div>
                                  
                                  <div className="flex items-center justify-between mb-2">
                                    <span className="text-xs font-semibold text-slate-500">AMOUNT</span>
                                    <span className="font-semibold text-green-600 text-sm">₹{order.amount / 100}</span>
                                  </div>
                                  
                                  <div className="flex items-center justify-between mb-2">
                                    <span className="text-xs font-semibold text-slate-500">STATUS</span>
                                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusClass(order.status)}`}>
                                      {order.status}
                                    </span>
                                  </div>
                                  
                                  {/* Razorpay IDs Section */}
                                  {(order.razorpay_order_id || order.razorpay_payment_id || order.razorpay_subscription_id) && (
                                    <div className="mt-2 pt-2 border-t border-slate-200">
                                      {order.razorpay_order_id && (
                                        <div className="mb-2">
                                          <p className="text-xs font-semibold text-slate-500 mb-1">RAZORPAY ORDER ID</p>
                                          <div className="flex items-center space-x-1">
                                            <p className="font-mono text-purple-700 bg-purple-50 px-2 py-1 rounded text-xs break-all flex-1" style={{fontSize: '10px'}}>
                                              {order.razorpay_order_id}
                                            </p>
                                            <button
                                              onClick={() => {
                                                navigator.clipboard.writeText(order.razorpay_order_id);
                                              }}
                                              className="text-slate-400 hover:text-slate-600 transition-colors flex-shrink-0"
                                              title="Copy Order ID"
                                            >
                                              <CreditCard className="w-3 h-3" />
                                            </button>
                                          </div>
                                        </div>
                                      )}
                                      
                                      {order.razorpay_payment_id && (
                                        <div className="mb-2">
                                          <p className="text-xs font-semibold text-slate-500 mb-1">RAZORPAY PAYMENT ID</p>
                                          <div className="flex items-center space-x-1">
                                            <p className="font-mono text-blue-700 bg-blue-50 px-2 py-1 rounded text-xs break-all flex-1" style={{fontSize: '10px'}}>
                                              {order.razorpay_payment_id}
                                            </p>
                                            <button
                                              onClick={() => {
                                                navigator.clipboard.writeText(order.razorpay_payment_id);
                                              }}
                                              className="text-slate-400 hover:text-slate-600 transition-colors flex-shrink-0"
                                              title="Copy Payment ID"
                                            >
                                              <CreditCard className="w-3 h-3" />
                                            </button>
                                          </div>
                                        </div>
                                      )}
                                      
                                      {order.razorpay_subscription_id && (
                                        <div>
                                          <p className="text-xs font-semibold text-slate-500 mb-1">RAZORPAY SUBSCRIPTION ID</p>
                                          <div className="flex items-center space-x-1">
                                            <p className="font-mono text-green-700 bg-green-50 px-2 py-1 rounded text-xs break-all flex-1" style={{fontSize: '10px'}}>
                                              {order.razorpay_subscription_id}
                                            </p>
                                            <button
                                              onClick={() => {
                                                navigator.clipboard.writeText(order.razorpay_subscription_id);
                                              }}
                                              className="text-slate-400 hover:text-slate-600 transition-colors flex-shrink-0"
                                              title="Copy Subscription ID"
                                            >
                                              <CreditCard className="w-3 h-3" />
                                            </button>
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                              </td>
                              
                              {/* License Details Column */}
                              <td className="px-4 py-4 text-sm" style={{minWidth: '260px', width: '260px'}}>
                                <div>
                                  <p className="font-semibold text-slate-900 text-xs mb-1">License Key:</p>
                                  {order.license_key ? (
                                    <p className="font-mono text-slate-600 text-xs break-all mb-2">
                                      {order.license_key}
                                    </p>
                                  ) : (
                                    <p className="text-slate-400 text-xs mb-2">
                                      {order.status === 'completed' ? 'Missing License Key' : 'Not Generated Yet'}
                                    </p>
                                  )}
                                  
                                  <p className="font-semibold text-slate-900 text-xs mb-1">Status:</p>
                                  {orderLicense ? (
                                    <div className="flex items-center space-x-2 mb-2">
                                      {getLicenseStatusIcon(orderLicense.status)}
                                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getLicenseStatusClass(orderLicense.status)}`}>
                                        {orderLicense.status}
                                      </span>
                                    </div>
                                  ) : (
                                    <span className={`text-xs mb-2 block ${order.status === 'completed' ? 'text-red-600' : 'text-gray-400'}`}>
                                      {order.status === 'completed' ? 'No license found' : 'Pending'}
                                    </span>
                                  )}
                                  
                                  {orderLicense && orderLicense.hardware_id && (
                                    <>
                                      <p className="font-semibold text-slate-900 text-xs mb-1">Hardware ID:</p>
                                      <p className="font-mono text-slate-500 text-xs break-all">
                                        {orderLicense.hardware_id}
                                      </p>
                                    </>
                                  )}
                                </div>
                              </td>
                              
                              {/* Subscription Info Column */}
                              <td className="px-4 py-4 text-sm" style={{minWidth: '200px', width: '200px'}}>
                                {(order.subscription_type === 'individual' || order.subscription_type === 'family') && order.razorpay_subscription_id ? (
                                  <div>
                                    <p className="font-semibold text-slate-900 text-xs mb-1">Status:</p>
                                    <span className={`px-2 py-1 rounded-full text-xs font-medium mb-2 inline-block ${
                                      order.subscription_status === 'active' ? 'bg-green-100 text-green-800' :
                                      order.subscription_status === 'cancelled' ? 'bg-red-100 text-red-800' :
                                      order.subscription_status === 'payment_failed' ? 'bg-yellow-100 text-yellow-800' :
                                      order.subscription_status === 'expired' ? 'bg-gray-100 text-gray-800' :
                                      'bg-blue-100 text-blue-800'
                                    }`}>
                                      {order.subscription_status || 'pending'}
                                    </span>
                                    
                                    {order.next_billing_date && (
                                      <>
                                        <p className="font-semibold text-slate-900 text-xs mb-1 mt-2">Next Billing:</p>
                                        <p className="text-slate-600 text-xs mb-2">
                                          {new Date(order.next_billing_date).toLocaleDateString('en-IN', {
                                            year: 'numeric',
                                            month: 'short',
                                            day: 'numeric'
                                          })}
                                        </p>
                                      </>
                                    )}
                                    
                                    <p className="font-semibold text-slate-900 text-xs mb-1">Auto-Renewal:</p>
                                    <span className={`px-2 py-1 rounded-full text-xs font-medium mb-2 inline-block ${
                                      order.auto_renew_enabled ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                                    }`}>
                                      {order.auto_renew_enabled ? 'Enabled' : 'Disabled'}
                                    </span>
                                    
                                    {order.grace_period_end_date && new Date(order.grace_period_end_date) > new Date() && (
                                      <>
                                        <p className="font-semibold text-yellow-700 text-xs mb-1 flex items-center mt-2">
                                          <Clock className="w-3 h-3 mr-1" />
                                          Grace Period:
                                        </p>
                                        <p className="text-yellow-700 text-xs">
                                          Ends {new Date(order.grace_period_end_date).toLocaleDateString('en-IN', {
                                            month: 'short',
                                            day: 'numeric'
                                          })}
                                        </p>
                                      </>
                                    )}
                                  </div>
                                ) : (
                                  <span className="text-xs text-slate-400">
                                    {order.subscription_type === 'beta' ? 'No Subscription' : 'Not Activated'}
                                  </span>
                                )}
                              </td>
                              
                              {/* Actions Column */}
                              <td className="px-4 py-4" style={{minWidth: '160px', width: '160px'}}>
                                <div className="flex flex-col space-y-1">
                                  <button
                                    onClick={() => openEmailComposer(order, orderUser)}
                                    className="inline-flex items-center px-3 py-2 border border-slate-300 shadow-sm text-xs font-medium rounded-md text-slate-700 bg-white hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 w-full justify-center"
                                    data-testid={`email-customer-${order.id}`}
                                    title={`Send email to ${orderUser?.email || 'customer'}`}
                                  >
                                    <Mail className="w-4 h-4 mr-1" />
                                    Email Customer
                                  </button>
                                  <button
                                    onClick={() => handleDeleteOrder(order.id)}
                                    disabled={actionLoading[`delete-${order.id}`]}
                                    className="inline-flex items-center px-3 py-2 border border-red-300 shadow-sm text-xs font-medium rounded-md text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 w-full justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                                    data-testid={`delete-order-${order.id}`}
                                    title={`Delete order ${formatOrderId(order.id)}`}
                                  >
                                    {actionLoading[`delete-${order.id}`] ? (
                                      <>
                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600 mr-1"></div>
                                        Deleting...
                                      </>
                                    ) : (
                                      <>
                                        <Trash2 className="w-4 h-4 mr-1" />
                                        Delete Order
                                      </>
                                    )}
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
                
                {/* Pagination Controls */}
                <PaginationControls data={filteredOrders} tabName="orders" />
              </div>
            )}

            {activeTab === 'top-customers' && (
              <div>
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-xl font-bold text-slate-900">Top Customers</h3>
                  <div className="flex items-center space-x-4">
                    <button
                      onClick={exportTopCustomers}
                      className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Export CSV
                    </button>
                    <div className="text-sm text-slate-600 bg-slate-50 px-4 py-2 rounded-lg">
                      Showing {paginatedTopCustomers.length} of {filteredTopCustomers.length} customers ({topCustomers.length} total)
                    </div>
                  </div>
                </div>
                
                {/* Search Filters */}
                <div className="bg-slate-50 rounded-lg p-4 mb-6">
                  <h4 className="text-sm font-semibold text-slate-700 mb-3">Search Top Customers</h4>
                  <div className="grid md:grid-cols-1 gap-4 max-w-md">
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">Customer Email</label>
                      <input
                        type="text"
                        placeholder="Search by customer email..."
                        className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        value={searchFilters.topCustomers.email}
                        onChange={(e) => setSearchFilters(prev => ({
                          ...prev,
                          topCustomers: { ...prev.topCustomers, email: e.target.value }
                        }))}
                      />
                    </div>
                  </div>
                  <button
                    onClick={() => setSearchFilters(prev => ({
                      ...prev,
                      topCustomers: { email: '' }
                    }))}
                    className="mt-3 px-4 py-2 bg-slate-600 text-white rounded-md text-sm hover:bg-slate-700 transition-colors"
                  >
                    Clear Search
                  </button>
                </div>
                
                {/* Mobile Cards */}
                <div className="grid gap-6 md:hidden">
                  {paginatedTopCustomers.map((customer, index) => {
                    // Calculate actual rank based on current page
                    const actualRank = (pagination.topCustomers.currentPage - 1) * pagination.topCustomers.itemsPerPage + index + 1;
                    return (
                    <div key={customer.user_id} className="bg-white border border-slate-200 rounded-lg p-4 shadow-sm">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center space-x-2">
                          <span className="bg-gradient-to-r from-purple-600 to-blue-600 text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center">
                            #{actualRank}
                          </span>
                          <span className="font-semibold text-slate-900">Top Customer</span>
                        </div>
                        <span className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs font-medium">
                          {customer.totalOrders} Orders
                        </span>
                      </div>
                      
                      <div className="space-y-3 text-sm">
                        <div>
                          <span className="font-medium text-slate-700">Customer:</span>
                          <p className="text-slate-900">{customer.user.name}</p>
                          <p className="text-slate-600 break-all text-xs">{customer.user.email}</p>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <span className="font-medium text-slate-700">Total Orders:</span>
                            <p className="text-slate-900 font-semibold">{customer.totalOrders}</p>
                          </div>
                          <div>
                            <span className="font-medium text-slate-700">Completed:</span>
                            <p className="text-green-600 font-semibold">{customer.completedOrders}</p>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <span className="font-medium text-slate-700">Total Revenue:</span>
                            <p className="text-blue-600 font-semibold">₹{(customer.totalRevenue / 100).toLocaleString()}</p>
                          </div>
                          <div>
                            <span className="font-medium text-slate-700">Earned Revenue:</span>
                            <p className="text-green-600 font-semibold">₹{(customer.completedRevenue / 100).toLocaleString()}</p>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <span className="font-medium text-slate-700">First Order:</span>
                            <p className="text-slate-600 text-xs">{new Date(customer.firstOrderDate).toLocaleDateString()}</p>
                          </div>
                          <div>
                            <span className="font-medium text-slate-700">Last Order:</span>
                            <p className="text-slate-600 text-xs">{new Date(customer.lastOrderDate).toLocaleDateString()}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                    );
                  })}
                </div>

                {/* Enhanced Desktop Table */}
                <div className="hidden md:block">
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-slate-200 table-fixed">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider" style={{width: '60px'}}>
                            Rank
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider" style={{width: '25%'}}>
                            Customer Details
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider" style={{width: '20%'}}>
                            Order Statistics
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider" style={{width: '25%'}}>
                            Revenue Statistics
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider" style={{width: '20%'}}>
                            Timeline
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-slate-200">
                        {paginatedTopCustomers.map((customer, index) => {
                          // Calculate actual rank based on current page
                          const actualRank = (pagination.topCustomers.currentPage - 1) * pagination.topCustomers.itemsPerPage + index + 1;
                          return (
                          <tr key={customer.user_id} data-testid={`admin-top-customer-${customer.user_id}`}>
                            <td className="px-4 py-4 text-sm text-center" style={{width: '60px'}}>
                              <div className="flex items-center justify-center">
                                <span className="bg-gradient-to-r from-purple-600 to-blue-600 text-white text-sm font-bold rounded-full w-8 h-8 flex items-center justify-center">
                                  #{actualRank}
                                </span>
                              </div>
                            </td>
                            <td className="px-4 py-4 text-sm" style={{width: '25%'}}>
                              <div>
                                <p className="font-semibold text-slate-900">{customer.user.name}</p>
                                <p className="text-slate-600 break-all text-xs mt-1">{customer.user.email}</p>
                              </div>
                            </td>
                            <td className="px-4 py-4 text-sm" style={{width: '20%'}}>
                              <div className="space-y-1">
                                <div className="flex items-center justify-between">
                                  <span className="text-slate-600 text-xs">Total:</span>
                                  <span className="font-semibold text-slate-900">{customer.totalOrders}</span>
                                </div>
                                <div className="flex items-center justify-between">
                                  <span className="text-slate-600 text-xs">Completed:</span>
                                  <span className="font-semibold text-green-600">{customer.completedOrders}</span>
                                </div>
                                <div className="flex items-center justify-between">
                                  <span className="text-slate-600 text-xs">Success Rate:</span>
                                  <span className="font-semibold text-blue-600">
                                    {customer.totalOrders > 0 ? Math.round((customer.completedOrders / customer.totalOrders) * 100) : 0}%
                                  </span>
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-4 text-sm" style={{width: '25%'}}>
                              <div className="space-y-1">
                                <div className="flex items-center justify-between">
                                  <span className="text-slate-600 text-xs">Total Revenue:</span>
                                  <span className="font-semibold text-blue-600">₹{(customer.totalRevenue / 100).toLocaleString()}</span>
                                </div>
                                <div className="flex items-center justify-between">
                                  <span className="text-slate-600 text-xs">Earned Revenue:</span>
                                  <span className="font-semibold text-green-600">₹{(customer.completedRevenue / 100).toLocaleString()}</span>
                                </div>
                                <div className="flex items-center justify-between">
                                  <span className="text-slate-600 text-xs">Avg per Order:</span>
                                  <span className="font-semibold text-slate-700">
                                    ₹{customer.completedOrders > 0 ? Math.round((customer.completedRevenue / customer.completedOrders) / 100).toLocaleString() : 0}
                                  </span>
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-4 text-sm" style={{width: '20%'}}>
                              <div className="space-y-1">
                                <div>
                                  <p className="text-slate-600 text-xs">First Order:</p>
                                  <p className="text-slate-700 text-xs">{new Date(customer.firstOrderDate).toLocaleDateString()}</p>
                                </div>
                                <div>
                                  <p className="text-slate-600 text-xs">Last Order:</p>
                                  <p className="text-slate-700 text-xs">{new Date(customer.lastOrderDate).toLocaleDateString()}</p>
                                </div>
                                <div>
                                  <p className="text-slate-600 text-xs">Customer Since:</p>
                                  <p className="text-slate-500 text-xs">
                                    {Math.ceil((Date.now() - customer.firstOrderDate) / (1000 * 60 * 60 * 24))} days
                                  </p>
                                </div>
                              </div>
                            </td>
                          </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  
                  {filteredTopCustomers.length === 0 && (
                    <div className="text-center py-8 text-slate-500">
                      <p>No customers found with multiple orders.</p>
                      <p className="text-xs mt-2">Customers need at least 2 orders to appear in this list.</p>
                    </div>
                  )}
                </div>
                
                {/* Pagination Controls */}
                <PaginationControls data={filteredTopCustomers} tabName="topCustomers" />
              </div>
            )}

            {activeTab === 'licenses' && (
              <div>
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-xl font-bold text-slate-900">License Management</h3>
                  <div className="flex items-center space-x-4">
                    <button
                      onClick={exportLicenses}
                      className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Export CSV
                    </button>
                    <div className="text-sm text-slate-600 bg-slate-50 px-4 py-2 rounded-lg">
                      Showing {paginatedLicenses.length} of {filteredLicenses.length} licenses ({licenses.length} total)
                    </div>
                  </div>
                </div>
                
                {/* Search Filters */}
                <div className="bg-slate-50 rounded-lg p-4 mb-6">
                  <h4 className="text-sm font-semibold text-slate-700 mb-3">Search & Filter Licenses</h4>
                  <div className="grid md:grid-cols-5 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">License Key</label>
                      <input
                        type="text"
                        placeholder="Enter license key..."
                        className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        value={searchFilters.licenses.licenseKey}
                        onChange={(e) => setSearchFilters(prev => ({
                          ...prev,
                          licenses: { ...prev.licenses, licenseKey: e.target.value }
                        }))}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">Customer Email</label>
                      <input
                        type="text"
                        placeholder="Search by email..."
                        className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        value={searchFilters.licenses.customer}
                        onChange={(e) => setSearchFilters(prev => ({
                          ...prev,
                          licenses: { ...prev.licenses, customer: e.target.value }
                        }))}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">Order ID</label>
                      <input
                        type="text"
                        placeholder="e.g. #16DF3940-99C"
                        className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        value={searchFilters.licenses.orderId}
                        onChange={(e) => setSearchFilters(prev => ({
                          ...prev,
                          licenses: { ...prev.licenses, orderId: e.target.value }
                        }))}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">Status</label>
                      <select
                        className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        value={searchFilters.licenses.status}
                        onChange={(e) => setSearchFilters(prev => ({
                          ...prev,
                          licenses: { ...prev.licenses, status: e.target.value }
                        }))}
                      >
                        <option value="">All Statuses</option>
                        <option value="activated">Activated</option>
                        <option value="unactivated">Unactivated</option>
                        <option value="expired">Expired</option>
                        <option value="deactivated">Deactivated</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">Plan Type</label>
                      <select
                        className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        value={searchFilters.licenses.planType}
                        onChange={(e) => setSearchFilters(prev => ({
                          ...prev,
                          licenses: { ...prev.licenses, planType: e.target.value }
                        }))}
                      >
                        <option value="">All Plans</option>
                        <option value="beta">Beta</option>
                        <option value="individual">Individual</option>
                        <option value="family">Family</option>
                        <option value="unknown">Unknown</option>
                      </select>
                    </div>
                  </div>
                  <button
                    onClick={() => setSearchFilters(prev => ({
                      ...prev,
                      licenses: { licenseKey: '', status: '', orderId: '', customer: '', planType: '' }
                    }))}
                    className="mt-3 px-4 py-2 bg-slate-600 text-white rounded-md text-sm hover:bg-slate-700 transition-colors"
                  >
                    Clear Filters
                  </button>
                </div>
                
                {/* Card Layout for Mobile */}
                <div className="grid gap-4 md:hidden">
                  {paginatedLicenses.map((license) => {
                    const licenseUser = users.find(user => user.id === license.user_id);
                    const licenseOrder = orders.find(order => order.id === license.order_id);
                    return (
                      <div key={license.license_key} className="bg-white border border-slate-200 rounded-lg p-4 shadow-sm">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center space-x-2">
                            {getLicenseStatusIcon(license.status)}
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getLicenseStatusClass(license.status)}`}>
                              {license.status}
                            </span>
                          </div>
                          {license.status === 'activated' && (
                            <button
                              onClick={() => handleLicenseAction(license.license_key, 'deactivate')}
                              disabled={actionLoading[license.license_key]}
                              className="bg-red-100 hover:bg-red-200 text-red-800 px-2 py-1 rounded text-xs font-medium"
                            >
                              {actionLoading[license.license_key] ? 'Deactivating...' : 'Deactivate'}
                            </button>
                          )}
                          {license.status === 'deactivated' && (
                            <button
                              onClick={() => handleLicenseAction(license.license_key, 'reactivate')}
                              disabled={actionLoading[license.license_key]}
                              className="bg-green-100 hover:bg-green-200 text-green-800 px-2 py-1 rounded text-xs font-medium"
                            >
                              {actionLoading[license.license_key] ? 'Reactivating...' : 'Reactivate'}
                            </button>
                          )}
                        </div>
                        
                        <div className="space-y-2 text-sm">
                          <div>
                            <span className="font-medium text-slate-700">License Key:</span>
                            <p className="font-mono text-slate-600 break-all text-xs mt-1">{license.license_key}</p>
                          </div>
                          <div>
                            <span className="font-medium text-slate-700">Order ID:</span>
                            <p className="font-mono text-slate-900 font-bold">{licenseOrder ? formatOrderId(licenseOrder.id) : 'Unknown'}</p>
                          </div>
                          <div>
                            <span className="font-medium text-slate-700">Customer Email:</span>
                            <p className="text-slate-600 break-all">{licenseUser?.email || 'Unknown User'}</p>
                          </div>
                          <div>
                            <span className="font-medium text-slate-700">Plan Type:</span>
                            <p className="text-slate-900 capitalize font-semibold">{license.subscription_type || 'Unknown'}</p>
                          </div>
                          <div>
                            <span className="font-medium text-slate-700">Hardware ID:</span>
                            <p className="font-mono text-slate-600 break-all text-xs">{license.hardware_id || 'Not activated yet'}</p>
                          </div>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <span className="font-medium text-slate-700">PAN Limit:</span>
                              <p className="text-slate-900 font-semibold">{license.pan_limit || 1}</p>
                            </div>
                            <div>
                              <span className="font-medium text-slate-700">Bound PANs:</span>
                              <div className="flex items-center space-x-2">
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                  (license.bound_pans?.length || 0) > 0 ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                                }`}>
                                  {license.bound_pans?.length || 0}
                                </span>
                                {(license.bound_pans?.length || 0) > 0 && (
                                  <button
                                    onClick={() => openPanManagementModal(license)}
                                    className="text-xs text-blue-600 hover:text-blue-800"
                                  >
                                    View
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <span className="font-medium text-slate-700">Activated:</span>
                              <p className="text-xs">{license.activation_date ? formatDate(license.activation_date) : 'Not activated'}</p>
                            </div>
                            <div>
                              <span className="font-medium text-slate-700">Expires:</span>
                              <p className="text-xs">
                                {license.expiration_date ? 
                                  <span className={isLicenseExpired(license.expiration_date) ? 'text-red-600' : 'text-slate-500'}>
                                    {formatDate(license.expiration_date)}
                                  </span>
                                  : 
                                  'Not set'
                                }
                              </p>
                            </div>
                          </div>
                          <div className="pt-2 border-t border-slate-200 space-y-2">
                            {(license.bound_pans?.length || 0) > 0 && (
                              <button
                                onClick={() => openPanManagementModal(license)}
                                className="w-full bg-blue-100 hover:bg-blue-200 text-blue-800 px-3 py-2 rounded text-xs font-medium flex items-center justify-center space-x-2"
                              >
                                <Settings className="w-4 h-4" />
                                <span>Manage PANs</span>
                              </button>
                            )}
                            <button
                              onClick={() => openEmailComposer(licenseOrder, licenseUser)}
                              className="w-full bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 px-3 py-2 rounded text-xs font-medium flex items-center justify-center space-x-2"
                              title={`Send email to ${licenseUser?.email || 'customer'}`}
                            >
                              <Mail className="w-4 h-4" />
                              <span>Email Customer</span>
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Enhanced Desktop Table */}
                <div className="hidden md:block">
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-slate-200 table-fixed">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider" style={{width: '22%', minWidth: '280px'}}>
                            License Details
                          </th>
                          <th className="px-3 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider" style={{width: '8%'}}>
                            Status
                          </th>
                          <th className="px-3 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider" style={{width: '10%'}}>
                            Plan Type
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider" style={{width: '14%'}}>
                            Hardware Binding
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider" style={{width: '10%'}}>
                            PAN Usage
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider" style={{width: '16%'}}>
                            Timeline
                          </th>
                          <th className="px-3 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider" style={{width: '20%'}}>
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-slate-200">
                        {paginatedLicenses.map((license) => {
                          const licenseUser = users.find(user => user.id === license.user_id);
                          const licenseOrder = orders.find(order => order.id === license.order_id);
                          return (
                            <tr key={license.license_key} data-testid={`admin-license-${license.license_key}`}>
                              <td className="px-4 py-4 text-sm" style={{width: '22%', minWidth: '280px'}}>
                                <div className="space-y-1">
                                  <div className="grid grid-cols-[auto,1fr] gap-x-2 items-start">
                                    <span className="font-semibold text-slate-900 text-xs whitespace-nowrap">License Key:</span>
                                    <span className="font-mono text-slate-700 break-all text-xs">{license.license_key}</span>
                                  </div>
                                  <div className="grid grid-cols-[auto,1fr] gap-x-2 items-start">
                                    <span className="font-semibold text-slate-900 text-xs whitespace-nowrap">Order ID:</span>
                                    <span className="font-mono text-slate-900 font-bold text-sm">{licenseOrder ? formatOrderId(licenseOrder.id) : 'Unknown'}</span>
                                  </div>
                                  <div className="grid grid-cols-[auto,1fr] gap-x-2 items-start">
                                    <span className="font-semibold text-slate-900 text-xs whitespace-nowrap">Customer:</span>
                                    <span className="text-slate-600 text-xs break-all">{licenseUser?.email || 'Unknown User'}</span>
                                  </div>
                                </div>
                              </td>
                              <td className="px-3 py-4">
                                <div className="flex items-center space-x-2">
                                  {getLicenseStatusIcon(license.status)}
                                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getLicenseStatusClass(license.status)}`}>
                                    {license.status}
                                  </span>
                                </div>
                              </td>
                              <td className="px-3 py-4">
                                <div className="text-center">
                                  <span className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${
                                    license.subscription_type === 'beta' ? 'bg-blue-100 text-blue-800' :
                                    license.subscription_type === 'individual' ? 'bg-green-100 text-green-800' :
                                    license.subscription_type === 'family' ? 'bg-purple-100 text-purple-800' :
                                    'bg-gray-100 text-gray-800'
                                  }`}>
                                    {license.subscription_type || 'Unknown'}
                                  </span>
                                </div>
                              </td>
                              <td className="px-4 py-4 text-sm" style={{width: '15%'}}>
                                <div>
                                  <p className="font-semibold text-slate-900 text-xs mb-1">Hardware ID:</p>
                                  {license.hardware_id ? (
                                    <p className="font-mono text-slate-600 break-all text-xs">{license.hardware_id}</p>
                                  ) : (
                                    <p className="text-slate-400 text-xs">Not activated yet</p>
                                  )}
                                </div>
                              </td>
                              <td className="px-4 py-4 text-sm" style={{width: '12%'}}>
                                <div>
                                  <p className="font-semibold text-slate-900 text-xs mb-1">PAN Limit:</p>
                                  <p className="text-slate-600 text-xs mb-2">{license.pan_limit || 1}</p>
                                  <p className="font-semibold text-slate-900 text-xs mb-1">Bound PANs:</p>
                                  <p className="text-xs">
                                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                      (license.bound_pans?.length || 0) > 0 ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                                    }`}>
                                      {license.bound_pans?.length || 0}
                                    </span>
                                  </p>
                                  {(license.bound_pans?.length || 0) > 0 && (
                                    <button
                                      onClick={() => openPanManagementModal(license)}
                                      className="mt-1 text-xs text-blue-600 hover:text-blue-800 flex items-center space-x-1"
                                    >
                                      <Eye className="w-3 h-3" />
                                      <span>View PANs</span>
                                    </button>
                                  )}
                                </div>
                              </td>
                              <td className="px-4 py-4 text-sm" style={{width: '15%'}}>
                                <div>
                                  <p className="font-semibold text-slate-900 text-xs mb-1">Activated:</p>
                                  <p className="text-xs text-slate-600 mb-2">
                                    {license.activation_date ? formatDate(license.activation_date) : 'Not activated'}
                                  </p>
                                  <p className="font-semibold text-slate-900 text-xs mb-1">Expires:</p>
                                  <p className="text-xs">
                                    {license.expiration_date ? 
                                      <span className={isLicenseExpired(license.expiration_date) ? 'text-red-600' : 'text-slate-600'}>
                                        {formatDate(license.expiration_date)}
                                      </span> : 
                                      'Not set'
                                    }
                                  </p>
                                </div>
                              </td>
                              <td className="px-3 py-4" style={{width: '22%'}}>
                                <div className="flex flex-col space-y-1">
                                  {license.status === 'activated' && (
                                    <button
                                      onClick={() => handleLicenseAction(license.license_key, 'deactivate')}
                                      disabled={actionLoading[license.license_key]}
                                      className="bg-red-100 hover:bg-red-200 text-red-800 px-2 py-1 rounded text-xs font-medium disabled:opacity-50 flex items-center space-x-1 justify-center"
                                    >
                                      <XCircle className="w-3 h-3" />
                                      <span>{actionLoading[license.license_key] ? 'Deactivating...' : 'Deactivate'}</span>
                                    </button>
                                  )}
                                  {license.status === 'deactivated' && (
                                    <button
                                      onClick={() => handleLicenseAction(license.license_key, 'reactivate')}
                                      disabled={actionLoading[license.license_key]}
                                      className="bg-green-100 hover:bg-green-200 text-green-800 px-2 py-1 rounded text-xs font-medium disabled:opacity-50 flex items-center space-x-1 justify-center"
                                    >
                                      <CheckCircle className="w-3 h-3" />
                                      <span>{actionLoading[license.license_key] ? 'Reactivating...' : 'Reactivate'}</span>
                                    </button>
                                  )}
                                  {(license.bound_pans?.length || 0) > 0 && (
                                    <button
                                      onClick={() => openPanManagementModal(license)}
                                      className="bg-blue-100 hover:bg-blue-200 text-blue-800 px-2 py-1 rounded text-xs font-medium flex items-center space-x-1 justify-center"
                                    >
                                      <Settings className="w-3 h-3" />
                                      <span>Manage PANs</span>
                                    </button>
                                  )}
                                  <button
                                    onClick={() => openEmailComposer(licenseOrder, licenseUser)}
                                    className="bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 px-2 py-1 rounded text-xs font-medium flex items-center space-x-1 justify-center"
                                    title={`Send email to ${licenseUser?.email || 'customer'}`}
                                  >
                                    <Mail className="w-3 h-3" />
                                    <span>Email Customer</span>
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Pagination Controls */}
                <PaginationControls data={filteredLicenses} tabName="licenses" />

                {/* Beta Trials section moved to separate tab */}
              </div>
            )}

            {activeTab === 'users' && (
              <div>
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-xl font-bold text-slate-900">All Users</h3>
                  <div className="flex items-center space-x-4">
                    <button
                      onClick={exportUsers}
                      className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Export CSV
                    </button>
                    <div className="text-sm text-slate-600 bg-slate-50 px-4 py-2 rounded-lg">
                      Showing {getPaginatedData(filteredUsers, 'users').length} of {filteredUsers.length} users ({users.length} total)
                    </div>
                  </div>
                </div>
                
                {/* Search Filters */}
                <div className="bg-slate-50 rounded-lg p-4 mb-6">
                  <h4 className="text-sm font-semibold text-slate-700 mb-3">Search & Filter Users</h4>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">Email</label>
                      <input
                        type="text"
                        placeholder="Enter email..."
                        className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        value={searchFilters.users.email}
                        onChange={(e) => setSearchFilters(prev => ({
                          ...prev,
                          users: { ...prev.users, email: e.target.value }
                        }))}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">Role</label>
                      <select
                        className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        value={searchFilters.users.role}
                        onChange={(e) => setSearchFilters(prev => ({
                          ...prev,
                          users: { ...prev.users, role: e.target.value }
                        }))}
                      >
                        <option value="">All Roles</option>
                        <option value="admin">Admin</option>
                        <option value="user">User</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">Email Status</label>
                      <select
                        className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        value={searchFilters.users.emailVerified}
                        onChange={(e) => setSearchFilters(prev => ({
                          ...prev,
                          users: { ...prev.users, emailVerified: e.target.value }
                        }))}
                      >
                        <option value="">All Status</option>
                        <option value="verified">✅ Verified</option>
                        <option value="unverified">❌ Not Verified</option>
                      </select>
                    </div>
                  </div>
                  <button
                    onClick={() => setSearchFilters(prev => ({
                      ...prev,
                      users: { email: '', role: '', emailVerified: '' }
                    }))}
                    className="mt-3 px-4 py-2 bg-slate-600 text-white rounded-md text-sm hover:bg-slate-700 transition-colors"
                  >
                    Clear Filters
                  </button>
                </div>
                
                {/* Card Layout for Mobile */}
                <div className="grid gap-4 md:hidden">
                  {getPaginatedData(filteredUsers, 'users').map((user) => (
                    <div key={user.id} className="bg-white border border-slate-200 rounded-lg p-4 shadow-sm">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center space-x-2">
                          <span className="font-semibold text-slate-900">{user.name}</span>
                          {user.is_admin && (
                            <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded-full text-xs font-medium">
                              Admin
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="space-y-2 text-sm">
                        <div>
                          <span className="font-medium text-slate-700">Email:</span>
                          <p className="text-slate-600 break-all">{user.email}</p>
                        </div>
                        <div>
                          <span className="font-medium text-slate-700">User ID:</span>
                          <p className="font-mono text-slate-500 break-all text-xs">{user.id}</p>
                        </div>
                        <div>
                          <span className="font-medium text-slate-700">Status:</span>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            user.is_active === false 
                              ? 'bg-red-100 text-red-800' 
                              : 'bg-green-100 text-green-800'
                          }`}>
                            {user.is_active === false ? 'Inactive' : 'Active'}
                          </span>
                        </div>
                        <div>
                          <span className="font-medium text-slate-700">Email Status:</span>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            user.is_email_verified 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {user.is_email_verified ? '✓ Verified' : '✗ Not Verified'}
                          </span>
                        </div>
                        <div>
                          <span className="font-medium text-slate-700">Joined:</span>
                          <p className="text-slate-600">{new Date(user.created_at).toLocaleDateString()}</p>
                        </div>
                        {!user.is_admin && (
                          <div className="mt-3 pt-3 border-t border-slate-200">
                            <div className="flex flex-col space-y-2">
                              <div className="flex space-x-2">
                                <button
                                  onClick={() => handleUserAction(user.id, user.is_active === false ? 'activate' : 'deactivate')}
                                  disabled={userActionLoading[user.id]}
                                  className={`flex-1 px-3 py-1 rounded text-xs font-medium transition-colors ${
                                    user.is_active === false
                                      ? 'bg-green-100 hover:bg-green-200 text-green-800'
                                      : 'bg-yellow-100 hover:bg-yellow-200 text-yellow-800'
                                  } disabled:opacity-50`}
                                >
                                  {userActionLoading[user.id] === 'activate' ? 'Activating...' : 
                                   userActionLoading[user.id] === 'deactivate' ? 'Deactivating...' :
                                   user.is_active === false ? 'Activate' : 'Deactivate'}
                                </button>
                                <button
                                  onClick={() => handleUserAction(user.id, 'delete')}
                                  disabled={userActionLoading[user.id]}
                                  className="flex-1 bg-red-100 hover:bg-red-200 text-red-800 px-3 py-1 rounded text-xs font-medium disabled:opacity-50 flex items-center justify-center space-x-1"
                                >
                                  <Trash2 className="w-3 h-3" />
                                  <span>{userActionLoading[user.id] === 'delete' ? 'Deleting...' : 'Delete'}</span>
                                </button>
                              </div>
                              <button
                                onClick={() => openEmailComposer(null, user)}
                                className="w-full bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 px-3 py-2 rounded text-xs font-medium flex items-center justify-center space-x-2"
                                title={`Send email to ${user.email}`}
                              >
                                <Mail className="w-4 h-4" />
                                <span>Email Customer</span>
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Enhanced Desktop Table */}
                <div className="hidden md:block">
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-slate-200 table-fixed">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider" style={{width: '220px'}}>
                            User Details
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider" style={{width: '280px'}}>
                            Email
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider" style={{width: '80px'}}>
                            Role
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider" style={{width: '120px'}}>
                            Email Status
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider" style={{width: '80px'}}>
                            Status
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider" style={{width: '120px'}}>
                            Joined Date
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider" style={{width: '180px'}}>
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-slate-200">
                        {getPaginatedData(filteredUsers, 'users').map((user) => (
                          <tr key={user.id} data-testid={`admin-user-${user.id}`}>
                            <td className="px-4 py-4 text-sm" style={{width: '220px'}}>
                              <div>
                                <p className="font-semibold text-slate-900 truncate">{user.name}</p>
                                <p className="font-mono text-slate-500 text-xs mt-1 truncate" title={user.id}>
                                  {user.id.substring(0, 8)}...
                                </p>
                              </div>
                            </td>
                            <td className="px-4 py-4 text-sm" style={{width: '280px'}}>
                              <p className="text-slate-900 break-words">{user.email}</p>
                            </td>
                            <td className="px-4 py-4 text-sm" style={{width: '80px'}}>
                              {user.is_admin ? (
                                <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded-full text-xs font-medium">
                                  Admin
                                </span>
                              ) : (
                                <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs font-medium">
                                  User
                                </span>
                              )}
                            </td>
                            <td className="px-4 py-4 text-sm" style={{width: '120px'}}>
                              {user.is_email_verified ? (
                                <span className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs font-medium">
                                  ✓ Verified
                                </span>
                              ) : (
                                <span className="bg-red-100 text-red-800 px-2 py-1 rounded-full text-xs font-medium">
                                  ✗ Not Verified
                                </span>
                              )}
                            </td>
                            <td className="px-4 py-4 text-sm" style={{width: '80px'}}>
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                user.is_active === false 
                                  ? 'bg-red-100 text-red-800' 
                                  : 'bg-green-100 text-green-800'
                              }`}>
                                {user.is_active === false ? 'Inactive' : 'Active'}
                              </span>
                            </td>
                            <td className="px-4 py-4 text-sm text-slate-500" style={{width: '120px'}}>
                              <span className="text-xs">{new Date(user.created_at).toLocaleDateString()}</span>
                            </td>
                            <td className="px-4 py-4 text-sm" style={{width: '180px'}}>
                              {!user.is_admin ? (
                                <div className="flex flex-col space-y-1">
                                  <button
                                    onClick={() => handleUserAction(user.id, user.is_active === false ? 'activate' : 'deactivate')}
                                    disabled={userActionLoading[user.id]}
                                    className={`w-full px-2 py-1 rounded text-xs font-medium transition-colors ${
                                      user.is_active === false
                                        ? 'bg-green-100 hover:bg-green-200 text-green-800'
                                        : 'bg-yellow-100 hover:bg-yellow-200 text-yellow-800'
                                    } disabled:opacity-50`}
                                  >
                                    {userActionLoading[user.id] === 'activate' ? 'Activating...' : 
                                     userActionLoading[user.id] === 'deactivate' ? 'Deactivating...' :
                                     user.is_active === false ? 'Activate' : 'Deactivate'}
                                  </button>
                                  <button
                                    onClick={() => handleUserAction(user.id, 'delete')}
                                    disabled={userActionLoading[user.id]}
                                    className="w-full bg-red-100 hover:bg-red-200 text-red-800 px-2 py-1 rounded text-xs font-medium disabled:opacity-50 flex items-center justify-center space-x-1"
                                  >
                                    <Trash2 className="w-3 h-3" />
                                    <span>{userActionLoading[user.id] === 'delete' ? 'Deleting...' : 'Delete'}</span>
                                  </button>
                                  <button
                                    onClick={() => openEmailComposer(null, user)}
                                    className="w-full bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 px-2 py-1 rounded text-xs font-medium flex items-center justify-center space-x-1"
                                    title={`Send email to ${user.email}`}
                                  >
                                    <Mail className="w-3 h-3" />
                                    <span>Email Customer</span>
                                  </button>
                                </div>
                              ) : (
                                <span className="text-gray-400 text-xs">Admin Protected</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
                
                {/* Pagination Controls */}
                <PaginationControls data={filteredUsers} tabName="users" />
              </div>
            )}

            {activeTab === 'beta-trials' && (
              <div>
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-xl font-bold text-slate-900">Beta Trial Usage Tracking</h3>
                  <div className="flex items-center space-x-4">
                    <button
                      onClick={exportBetaTrials}
                      className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Export CSV
                    </button>
                    <div className="text-sm text-slate-600 bg-slate-50 px-4 py-2 rounded-lg">
                      Showing {paginatedBetaTrials.length} of {filteredBetaTrials.length} trials ({betaTrials.length} total)
                    </div>
                  </div>
                </div>
                
                {/* Search Filters */}
                <div className="bg-slate-50 rounded-lg p-4 mb-6">
                  <h4 className="text-sm font-semibold text-slate-700 mb-3">Search & Filter Beta Trials</h4>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">Customer Email</label>
                      <input
                        type="text"
                        placeholder="Search by email..."
                        className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        value={searchFilters.betaTrials.email}
                        onChange={(e) => setSearchFilters(prev => ({
                          ...prev,
                          betaTrials: { ...prev.betaTrials, email: e.target.value }
                        }))}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">License Key</label>
                      <input
                        type="text"
                        placeholder="Search by license key..."
                        className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        value={searchFilters.betaTrials.licenseKey}
                        onChange={(e) => setSearchFilters(prev => ({
                          ...prev,
                          betaTrials: { ...prev.betaTrials, licenseKey: e.target.value }
                        }))}
                      />
                    </div>
                  </div>
                  <button
                    onClick={() => setSearchFilters(prev => ({
                      ...prev,
                      betaTrials: { email: '', licenseKey: '' }
                    }))}
                    className="mt-3 px-4 py-2 bg-slate-600 text-white rounded-md text-sm hover:bg-slate-700 transition-colors"
                  >
                    Clear Filters
                  </button>
                </div>
                
                {/* Mobile Cards */}
                <div className="grid gap-4 md:hidden">
                  {paginatedBetaTrials.map((trial, index) => (
                    <div key={index} className="bg-white border border-slate-200 rounded-lg p-4 shadow-sm">
                      <div className="space-y-3 text-sm">
                        <div>
                          <span className="font-medium text-slate-700">Customer Email:</span>
                          <p className="text-slate-900 break-all">{trial.email}</p>
                        </div>
                        <div>
                          <span className="font-medium text-slate-700">License Key:</span>
                          <p className="font-mono text-slate-600 break-all text-xs">{trial.license_key}</p>
                        </div>
                        <div>
                          <span className="font-medium text-slate-700">Hardware ID:</span>
                          <p className="font-mono text-slate-600 break-all text-xs">{trial.hardware_id}</p>
                        </div>
                        <div>
                          <span className="font-medium text-slate-700">Activated On:</span>
                          <p className="text-slate-600">{formatDate(trial.activation_date)}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Enhanced Desktop Table */}
                <div className="hidden md:block">
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-slate-200 table-fixed">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider" style={{minWidth: '200px'}}>
                            Customer Details
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider" style={{minWidth: '300px'}}>
                            License Key
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider" style={{minWidth: '250px'}}>
                            Hardware Binding
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider" style={{minWidth: '180px'}}>
                            Activation Date
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-slate-200">
                        {paginatedBetaTrials.map((trial, index) => (
                          <tr key={index} data-testid={`admin-beta-trial-${index}`}>
                            <td className="px-4 py-4 text-sm" style={{minWidth: '280px'}}>
                              <div>
                                <p className="font-semibold text-slate-900 text-xs mb-1">Customer Email:</p>
                                <p className="text-slate-700 break-all">{trial.email}</p>
                              </div>
                            </td>
                            <td className="px-4 py-4 text-sm" style={{minWidth: '300px'}}>
                              <div>
                                <p className="font-semibold text-slate-900 text-xs mb-1">License Key:</p>
                                <p className="font-mono text-slate-600 break-all text-xs">{trial.license_key}</p>
                              </div>
                            </td>
                            <td className="px-4 py-4 text-sm" style={{minWidth: '250px'}}>
                              <div>
                                <p className="font-semibold text-slate-900 text-xs mb-1">Hardware ID:</p>
                                <p className="font-mono text-slate-600 break-all text-xs">{trial.hardware_id}</p>
                              </div>
                            </td>
                            <td className="px-4 py-4 text-sm text-slate-500" style={{minWidth: '180px'}}>
                              <div>
                                <p className="font-semibold text-slate-900 text-xs mb-1">Activated:</p>
                                <p className="text-slate-600 text-xs">{formatDate(trial.activation_date)}</p>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
                
                {/* Pagination Controls */}
                <PaginationControls data={filteredBetaTrials} tabName="betaTrials" />
              </div>
            )}

            {activeTab === 'feedback' && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-2xl font-bold text-slate-900">Customer Feedback</h2>
                    <p className="text-slate-600">Monitor customer satisfaction and feature requests</p>
                  </div>
                  <div className="bg-blue-50 px-4 py-2 rounded-lg">
                    <span className="text-blue-700 font-semibold">{feedback.length} Total Responses</span>
                  </div>
                </div>

                {/* Feedback Analytics Cards */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
                  <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm">
                    <div className="flex items-center space-x-3">
                      <div className="bg-yellow-100 rounded-full p-2">
                        <Star className="w-5 h-5 text-yellow-600" />
                      </div>
                      <div>
                        <p className="text-sm text-slate-600">Average Rating</p>
                        <p className="text-2xl font-bold text-slate-900">
                          {feedback.length > 0 ? (feedback.reduce((sum, f) => sum + f.overall_rating, 0) / feedback.length).toFixed(1) : '0.0'}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm">
                    <div className="flex items-center space-x-3">
                      <div className="bg-blue-100 rounded-full p-2">
                        <BarChart3 className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-sm text-slate-600">Total Feedback</p>
                        <p className="text-xs text-slate-500 mb-1">All Time</p>
                        <p className="text-2xl font-bold text-slate-900">{feedback.length}</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm">
                    <div className="flex items-center space-x-3">
                      <div className="bg-green-100 rounded-full p-2">
                        <MessageSquare className="w-5 h-5 text-green-600" />
                      </div>
                      <div>
                        <p className="text-sm text-slate-600">This Month</p>
                        <p className="text-2xl font-bold text-slate-900">
                          {feedback.filter(f => new Date(f.created_at).getMonth() === new Date().getMonth()).length}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm">
                    <div className="flex items-center space-x-3">
                      <div className="bg-purple-100 rounded-full p-2">
                        <TrendingUp className="w-5 h-5 text-purple-600" />
                      </div>
                      <div>
                        <p className="text-sm text-slate-600">This Week</p>
                        <p className="text-2xl font-bold text-slate-900">
                          {feedback.filter(f => {
                            const feedbackDate = new Date(f.created_at);
                            const weekAgo = new Date();
                            weekAgo.setDate(weekAgo.getDate() - 7);
                            return feedbackDate >= weekAgo;
                          }).length}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Star Rating Breakdown */}
                <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 mb-6">
                  <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center space-x-2">
                    <Star className="w-5 h-5 text-yellow-500" />
                    <span>Rating Distribution</span>
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    {[5, 4, 3, 2, 1].map(rating => {
                      const count = feedback.filter(f => f.overall_rating === rating).length;
                      const percentage = feedback.length > 0 ? ((count / feedback.length) * 100).toFixed(1) : 0;
                      const colors = {
                        5: 'bg-green-100 text-green-800 border-green-200',
                        4: 'bg-blue-100 text-blue-800 border-blue-200', 
                        3: 'bg-yellow-100 text-yellow-800 border-yellow-200',
                        2: 'bg-orange-100 text-orange-800 border-orange-200',
                        1: 'bg-red-100 text-red-800 border-red-200'
                      };
                      
                      return (
                        <div key={rating} className={`${colors[rating]} rounded-lg p-4 border-2 text-center transition-all hover:shadow-md`}>
                          <div className="flex items-center justify-center space-x-1 mb-2">
                            <Star className="w-4 h-4 fill-current" />
                            <span className="font-bold text-lg">{rating}</span>
                          </div>
                          <div className="space-y-1">
                            <p className="text-2xl font-bold">{count}</p>
                            <p className="text-xs opacity-75">{percentage}%</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  
                  {/* Visual Rating Bar */}
                  <div className="mt-6">
                    <div className="flex items-center justify-between text-sm text-slate-600 mb-2">
                      <span>Rating Breakdown</span>
                      <span>{feedback.length} total reviews</span>
                    </div>
                    <div className="flex rounded-full overflow-hidden h-3 bg-slate-200">
                      {[5, 4, 3, 2, 1].map(rating => {
                        const count = feedback.filter(f => f.overall_rating === rating).length;
                        const percentage = feedback.length > 0 ? (count / feedback.length) * 100 : 0;
                        const colors = {
                          5: 'bg-green-500',
                          4: 'bg-blue-500', 
                          3: 'bg-yellow-500',
                          2: 'bg-orange-500',
                          1: 'bg-red-500'
                        };
                        
                        return percentage > 0 ? (
                          <div 
                            key={rating}
                            className={`${colors[rating]} transition-all duration-300`}
                            style={{ width: `${percentage}%` }}
                            title={`${rating} stars: ${count} reviews (${percentage.toFixed(1)}%)`}
                          />
                        ) : null;
                      })}
                    </div>
                    <div className="flex justify-between text-xs text-slate-500 mt-2">
                      <span>🌟 Excellent</span>
                      <span>😟 Poor</span>
                    </div>
                  </div>
                </div>

                {/* Feedback Filters */}
                <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Customer</label>
                      <input
                        type="text"
                        placeholder="Search by customer email..."
                        value={searchFilters.feedback.customer}
                        onChange={(e) => setSearchFilters(prev => ({
                          ...prev,
                          feedback: { ...prev.feedback, customer: e.target.value }
                        }))}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Rating</label>
                      <select
                        value={searchFilters.feedback.rating}
                        onChange={(e) => setSearchFilters(prev => ({
                          ...prev,
                          feedback: { ...prev.feedback, rating: e.target.value }
                        }))}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="">All Ratings</option>
                        <option value="5">5 Stars</option>
                        <option value="4">4 Stars</option>
                        <option value="3">3 Stars</option>
                        <option value="2">2 Stars</option>
                        <option value="1">1 Star</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Date</label>
                      <input
                        type="date"
                        value={searchFilters.feedback.date}
                        onChange={(e) => setSearchFilters(prev => ({
                          ...prev,
                          feedback: { ...prev.feedback, date: e.target.value }
                        }))}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>
                  <div className="flex justify-between items-center mt-4">
                    <button
                      onClick={() => setSearchFilters(prev => ({
                        ...prev,
                        feedback: { customer: '', rating: '', date: '' }
                      }))}
                      className="px-4 py-2 text-slate-600 border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
                    >
                      Clear Filters
                    </button>
                    <button
                      onClick={exportFeedbackToCSV}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
                    >
                      <Download className="w-4 h-4" />
                      <span>Export CSV</span>
                    </button>
                  </div>
                </div>

                {/* Feedback List */}
                <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                  <div className="p-6 border-b border-slate-200">
                    <h3 className="text-lg font-semibold text-slate-900">All Feedback ({filteredFeedback.length})</h3>
                  </div>
                  
                  <div className="divide-y divide-slate-200">
                    {paginatedFeedback.map((item) => (
                      <div key={item.id} className="p-6 hover:bg-slate-50">
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex items-center space-x-3">
                            <div className="bg-blue-100 rounded-full p-2">
                              <User className="w-4 h-4 text-blue-600" />
                            </div>
                            <div>
                              <h4 className="font-medium text-slate-900">{item.customer_name}</h4>
                              <p className="text-sm text-slate-600">{item.customer_email}</p>
                            </div>
                          </div>
                          <div className="flex items-center space-x-3">
                            <div className="text-right">
                              <div className="flex items-center space-x-1">
                                {[...Array(5)].map((_, i) => (
                                  <Star
                                    key={i}
                                    className={`w-4 h-4 ${
                                      i < item.overall_rating ? 'text-yellow-400 fill-current' : 'text-gray-300'
                                    }`}
                                  />
                                ))}
                              </div>
                              <p className="text-xs text-slate-500">
                                {new Date(item.created_at).toLocaleDateString()}
                              </p>
                            </div>
                            <button
                              onClick={() => handleDeleteFeedback(item.id, item.customer_name, item.customer_email)}
                              className="bg-red-50 hover:bg-red-100 text-red-600 p-2 rounded-lg transition-colors border border-red-200 hover:border-red-300"
                              title={`Delete feedback from ${item.customer_name}`}
                              data-testid={`delete-feedback-${item.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>

                        {/* Ratings Grid */}
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4 bg-slate-50 rounded-lg p-3">
                          <div className="text-center">
                            <p className="text-xs text-slate-600">Ease of Use</p>
                            <p className="font-semibold text-slate-900">{item.ease_of_use_rating}/5</p>
                          </div>
                          <div className="text-center">
                            <p className="text-xs text-slate-600">Accuracy</p>
                            <p className="font-semibold text-slate-900">{item.accuracy_rating}/5</p>
                          </div>
                          <div className="text-center">
                            <p className="text-xs text-slate-600">Support</p>
                            <p className="font-semibold text-slate-900">{item.support_rating}/5</p>
                          </div>
                        </div>

                        {/* Comments and Features */}
                        {item.comments && (
                          <div className="mb-3">
                            <h5 className="text-sm font-medium text-slate-900 mb-1">Comments:</h5>
                            <p className="text-sm text-slate-700 bg-blue-50 p-3 rounded-lg">{item.comments}</p>
                          </div>
                        )}

                        {item.features_liked && (
                          <div className="mb-3">
                            <h5 className="text-sm font-medium text-slate-900 mb-1">Features Liked:</h5>
                            <p className="text-sm text-slate-700 bg-green-50 p-3 rounded-lg">{item.features_liked}</p>
                          </div>
                        )}

                        {item.future_requirements && (
                          <div>
                            <h5 className="text-sm font-medium text-slate-900 mb-1">Future Requests:</h5>
                            <p className="text-sm text-slate-700 bg-purple-50 p-3 rounded-lg">{item.future_requirements}</p>
                          </div>
                        )}
                      </div>
                    ))}

                    {filteredFeedback.length === 0 && (
                      <div className="p-12 text-center">
                        <MessageSquare className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-slate-900 mb-2">No Feedback Found</h3>
                        <p className="text-slate-500">No customer feedback matches your current filters.</p>
                      </div>
                    )}
                  </div>

                  {/* Pagination Controls */}
                  <PaginationControls data={filteredFeedback} tabName="feedback" />
                </div>
              </div>
            )}

            {activeTab === 'software-management' && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-2xl font-bold text-slate-900">Software Management</h2>
                    <p className="text-slate-600">Upload and manage software bundles for customer downloads</p>
                  </div>
                  <div className="bg-blue-50 px-4 py-2 rounded-lg">
                    <span className="text-blue-700 font-semibold">{s3Files.length} Files</span>
                  </div>
                </div>

                {/* Upload Section */}
                <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
                  <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center space-x-2">
                    <Download className="w-5 h-5 text-blue-600" />
                    <span>Upload Software Bundle</span>
                  </h3>
                  
                  {/* Drag & Drop Upload Area */}
                  <div
                    className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                      dragActive 
                        ? 'border-blue-400 bg-blue-50' 
                        : 'border-slate-300 hover:border-slate-400'
                    }`}
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                  >
                    <Download className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                    <div className="space-y-2">
                      <p className="text-lg font-medium text-slate-900">
                        {dragActive ? 'Drop file here' : 'Drag & drop your file here'}
                      </p>
                      <p className="text-sm text-slate-600">
                        or{' '}
                        <label className="text-blue-600 hover:text-blue-700 cursor-pointer font-medium">
                          browse files
                          <input
                            type="file"
                            className="hidden"
                            accept=".zip,.exe,.msi,.tar.gz,.7z"
                            onChange={handleFileSelect}
                            disabled={uploadLoading}
                          />
                        </label>
                      </p>
                      <p className="text-xs text-slate-500">
                        Supported formats: ZIP, EXE, MSI, TAR.GZ, 7Z (Max 500MB)
                      </p>
                    </div>
                  </div>

                  {/* Upload Progress */}
                  {uploadLoading && (
                    <div className="mt-6">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-slate-700">Uploading...</span>
                        <span className="text-sm text-slate-600">{uploadProgress}%</span>
                      </div>
                      <div className="w-full bg-slate-200 rounded-full h-2">
                        <div 
                          className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                          style={{ width: `${uploadProgress}%` }}
                        ></div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Current Files Section */}
                <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
                  <div className="p-6 border-b border-slate-200">
                    <h3 className="text-lg font-semibold text-slate-900 flex items-center space-x-2">
                      <Key className="w-5 h-5 text-green-600" />
                      <span>Current Software Files</span>
                    </h3>
                  </div>
                  
                  <div className="divide-y divide-slate-200">
                    {s3Files.length === 0 ? (
                      <div className="p-8 text-center">
                        <Download className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                        <p className="text-slate-600">No files uploaded yet</p>
                        <p className="text-sm text-slate-500">Upload your first software bundle to get started</p>
                      </div>
                    ) : (
                      s3Files.map((file, index) => (
                        <div key={index} className="p-6 flex items-center justify-between">
                          <div className="flex items-center space-x-4">
                            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                              <Download className="w-6 h-6 text-blue-600" />
                            </div>
                            <div>
                              <p className="font-medium text-slate-900">{file.filename}</p>
                              <div className="flex items-center space-x-4 text-sm text-slate-600">
                                <span>{formatFileSize(file.size)}</span>
                                <span>•</span>
                                <span>{new Date(file.last_modified).toLocaleDateString()}</span>
                              </div>
                            </div>
                          </div>
                          
                          <div className="flex items-center space-x-3">
                            <button
                              onClick={() => {
                                setShowConfirmModal(true);
                                setConfirmAction({
                                  type: 'deleteFile',
                                  filename: file.filename,
                                  action: () => handleDeleteFile(file.filename)
                                });
                              }}
                              className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                              title="Delete File"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>

                {/* Instructions */}
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <h4 className="font-medium text-yellow-800">Upload Guidelines</h4>
                      <ul className="text-sm text-yellow-700 mt-1 space-y-1">
                        <li>• Upload compressed software bundles for customer downloads</li>
                        <li>• Files are stored securely in S3 with automatic signed URL generation</li>
                        <li>• Maximum file size: 500MB</li>
                        <li>• Supported formats: ZIP, EXE, MSI, TAR.GZ, 7Z</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={showConfirmModal}
        onClose={() => {
          setShowConfirmModal(false);
          setConfirmAction(null);
        }}
        onConfirm={
          confirmAction?.type === 'deleteFile' ? confirmDeleteFile :
          confirmAction?.type === 'deleteFeedback' ? confirmDeleteFeedback :
          confirmAction?.type === 'deleteUser' ? confirmUserAction :
          confirmAction?.type === 'userAction' ? confirmUserAction :
          confirmAction?.type === 'deleteOrder' ? confirmDeleteOrder :
          confirmAction?.type === 'licenseAction' ? confirmLicenseAction :
          null
        }
        title={
          confirmAction?.type === 'success' ? 'Success' :
          confirmAction?.type === 'error' ? 'Action Failed' :
          confirmAction?.type === 'deleteFile' ? 'Delete File' :
          confirmAction?.type === 'deleteUser' ? 'Delete User' :
          confirmAction?.type === 'userAction' ? `${confirmAction.action?.charAt(0).toUpperCase() + confirmAction.action?.slice(1)} User` :
          confirmAction?.type === 'deleteOrder' ? 'Delete Order' :
          confirmAction?.type === 'licenseAction' ? `${confirmAction.action?.charAt(0).toUpperCase() + confirmAction.action?.slice(1)} License` :
          confirmAction?.type === 'deleteFeedback' ? 'Delete Feedback' :
          'Confirm Action'
        }
        message={
          confirmAction?.type === 'success' ? (
            <div>
              <p className="text-green-700 mb-3">{confirmAction.message}</p>
              {confirmAction.details && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                  <p className="text-sm text-green-700">{confirmAction.details}</p>
                </div>
              )}
            </div>
          ) : confirmAction?.type === 'error' ? (
            <div>
              <p className="mb-3 text-red-700">{confirmAction.message}</p>
              {confirmAction.customerName && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="font-medium text-red-900">Customer: {confirmAction.customerName}</p>
                  <p className="text-sm text-red-700">{confirmAction.customerEmail}</p>
                </div>
              )}
              {confirmAction.userEmail && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="font-medium text-red-900">User: {confirmAction.userEmail}</p>
                </div>
              )}
              {confirmAction.orderDetails && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="font-medium text-red-900">Order: {confirmAction.orderDetails.id}</p>
                  <p className="text-sm text-red-700">Customer: {confirmAction.orderDetails.customer}</p>
                </div>
              )}
              {confirmAction.licenseDetails && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="font-medium text-red-900">License: {confirmAction.licenseDetails.key}</p>
                  <p className="text-sm text-red-700">Customer: {confirmAction.licenseDetails.customer}</p>
                </div>
              )}
              <p className="text-sm text-slate-600 mt-3">Please try again or contact support if the issue persists.</p>
            </div>
          ) : confirmAction?.type === 'deleteUser' ? (
            <div>
              <p className="mb-3">⚠️ WARNING: Deleting this user will permanently remove:</p>
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-3">
                <p className="font-medium text-red-900">User: {confirmAction.userEmail}</p>
                <ul className="text-sm text-red-700 mt-2 space-y-1">
                  <li>• User account</li>
                  <li>• All their orders</li>
                  <li>• All their license keys</li>
                  <li>• All associated data</li>
                </ul>
              </div>
              <p className="text-sm text-slate-600">This action cannot be undone. Are you absolutely sure?</p>
            </div>
          ) : confirmAction?.type === 'userAction' ? (
            <div>
              <p className="mb-3">Are you sure you want to {confirmAction.action} this user?</p>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="font-medium text-blue-900">User: {confirmAction.userEmail}</p>
              </div>
            </div>
          ) : confirmAction?.type === 'deleteOrder' ? (
            <div>
              <p className="mb-3">⚠️ WARNING: Delete this order?</p>
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-3">
                <p className="font-medium text-red-900">Order: {confirmAction.orderDetails?.id}</p>
                <p className="text-sm text-red-700">Customer: {confirmAction.orderDetails?.customer}</p>
                <p className="text-sm text-red-700">Plan: {confirmAction.orderDetails?.plan}</p>
                <p className="text-sm text-red-700">Amount: ₹{confirmAction.orderDetails?.amount}</p>
                {confirmAction.orderDetails?.subscriptionId && (
                  <p className="text-sm text-red-700">Subscription: {confirmAction.orderDetails?.subscriptionId}</p>
                )}
              </div>
              
              {confirmAction.orderDetails?.subscriptionId && 
               ['active', 'pending', 'created'].includes(confirmAction.orderDetails?.subscriptionStatus) && (
                <>
                  <div className="bg-orange-50 border border-orange-300 rounded-lg p-3 mb-3">
                    <p className="text-sm font-semibold text-orange-900 flex items-center mb-2">
                      <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd"/>
                      </svg>
                      Choose Subscription Cancellation Type
                    </p>
                    
                    <div className="space-y-2">
                      <label className="flex items-start cursor-pointer">
                        <input
                          type="radio"
                          name="cancellationType"
                          value="immediate"
                          checked={subscriptionCancellationType === 'immediate'}
                          onChange={(e) => setSubscriptionCancellationType(e.target.value)}
                          className="mt-1 mr-2"
                        />
                        <div>
                          <p className="text-sm font-medium text-orange-900">Immediate Cancellation (Recommended for fraud/abuse)</p>
                          <p className="text-xs text-orange-700">Customer loses access immediately. May require refund if recently paid.</p>
                        </div>
                      </label>
                      
                      <label className="flex items-start cursor-pointer">
                        <input
                          type="radio"
                          name="cancellationType"
                          value="end_of_cycle"
                          checked={subscriptionCancellationType === 'end_of_cycle'}
                          onChange={(e) => setSubscriptionCancellationType(e.target.value)}
                          className="mt-1 mr-2"
                        />
                        <div>
                          <p className="text-sm font-medium text-orange-900">End of Billing Cycle (Fair to customer)</p>
                          <p className="text-xs text-orange-700">Customer keeps access until their paid period ends. No refund needed.</p>
                        </div>
                      </label>
                    </div>
                  </div>
                </>
              )}
              
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-3">
                <p className="text-sm text-yellow-800">This will permanently remove:</p>
                <ul className="text-sm text-yellow-700 mt-1 space-y-1">
                  <li>• Order record</li>
                  <li>• Associated license key</li>
                  <li>• Beta trial usage data (if applicable)</li>
                  {confirmAction.orderDetails?.subscriptionId && 
                   ['active', 'pending', 'created'].includes(confirmAction.orderDetails?.subscriptionStatus) && (
                    <li className="font-semibold">
                      • Active Razorpay subscription (
                      {subscriptionCancellationType === 'immediate' 
                        ? 'cancelled immediately' 
                        : 'cancelled at end of billing cycle'}
                      )
                    </li>
                  )}
                </ul>
                <p className="text-sm text-yellow-800 mt-2">User account will remain intact.</p>
              </div>
              <p className="text-sm text-slate-600">This action cannot be undone.</p>
            </div>
          ) : confirmAction?.type === 'licenseAction' ? (
            <div>
              <p className="mb-3">Are you sure you want to {confirmAction.action} this license?</p>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="font-medium text-blue-900">License: {confirmAction.licenseDetails?.key}</p>
                <p className="text-sm text-blue-700">Customer: {confirmAction.licenseDetails?.customer}</p>
                <p className="text-sm text-blue-700">Current Status: {confirmAction.licenseDetails?.status}</p>
              </div>
            </div>
          ) : confirmAction?.type === 'deleteFile' ? (
            <div>
              <p className="mb-3">Are you sure you want to delete this file?</p>
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="font-medium text-red-900">File: {confirmAction.filename}</p>
              </div>
              <p className="text-sm text-slate-600 mt-3">This action cannot be undone.</p>
            </div>
          ) : confirmAction?.type === 'deleteFeedback' ? (
            <div>
              <p className="mb-3">Are you sure you want to delete this feedback?</p>
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="font-medium text-red-900">Customer: {confirmAction.customerName}</p>
                <p className="text-sm text-red-700">{confirmAction.customerEmail}</p>
              </div>
              <p className="text-sm text-slate-600 mt-3">This action cannot be undone.</p>
            </div>
          ) : 'Are you sure you want to proceed?'
        }
        confirmText={
          confirmAction?.type === 'success' || confirmAction?.type === 'error' ? null :
          confirmAction?.type === 'deleteFile' ? 'Delete File' :
          confirmAction?.type === 'deleteUser' ? 'Delete User' :
          confirmAction?.type === 'userAction' ? `${confirmAction.action?.charAt(0).toUpperCase() + confirmAction.action?.slice(1)} User` :
          confirmAction?.type === 'deleteOrder' ? 'Delete Order' :
          confirmAction?.type === 'licenseAction' ? `${confirmAction.action?.charAt(0).toUpperCase() + confirmAction.action?.slice(1)} License` :
          confirmAction?.type === 'deleteFeedback' ? 'Delete Feedback' :
          'Confirm'
        }
        cancelText={
          confirmAction?.type === 'success' ? 'Close' :
          confirmAction?.type === 'error' ? 'Close' :
          'Cancel'
        }
        type={
          confirmAction?.type === 'success' ? 'success' :
          confirmAction?.type === 'error' ? 'danger' :
          confirmAction?.type === 'deleteFile' || confirmAction?.type === 'deleteUser' || confirmAction?.type === 'deleteOrder' || confirmAction?.type === 'deleteFeedback' ? 'danger' :
          'warning'
        }
        isLoading={isDeleting}
      />

      {/* Notification Component */}
      {notification && (
        <div className="fixed top-20 left-1/2 transform -translate-x-1/2 z-50 transition-all duration-300 ease-in-out">
          <div className={`w-96 max-w-full bg-white shadow-xl rounded-xl pointer-events-auto flex ring-1 ring-black ring-opacity-5 ${
            notification.type === 'success' ? 'border-l-4 border-green-500' : 'border-l-4 border-red-500'
          }`}>
            <div className="flex-1 w-0 p-5">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  {notification.type === 'success' ? (
                    <CheckCircle className="h-7 w-7 text-green-500" />
                  ) : (
                    <XCircle className="h-7 w-7 text-red-500" />
                  )}
                </div>
                <div className="ml-4 flex-1">
                  <p className="text-base font-semibold text-gray-900">
                    {notification.title}
                  </p>
                  <p className="mt-2 text-sm text-gray-600 leading-relaxed">
                    {notification.message}
                  </p>
                </div>
              </div>
            </div>
            <div className="flex border-l border-gray-200">
              <button
                onClick={() => setNotification(null)}
                className="w-full border border-transparent rounded-none rounded-r-xl p-4 flex items-center justify-center text-sm font-medium text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
              >
                <XCircle className="h-6 w-6" />
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Email Composer Modal */}
      <EmailComposerModal
        isOpen={emailComposerModal.isOpen}
        onClose={closeEmailComposer}
        customerEmail={emailComposerModal.customerEmail}
        customerName={emailComposerModal.customerName}
        orderData={emailComposerModal.orderData}
        onEmailSent={handleEmailSent}
      />

      {/* Bulk Email Modal */}
      <BulkEmailModal
        isOpen={bulkEmailModalOpen}
        onClose={() => setBulkEmailModalOpen(false)}
        onEmailSent={handleEmailSent}
      />

      {/* PAN Management Modal */}
      {panManagementModal.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <div>
                <h2 className="text-xl font-bold text-slate-900">PAN Management</h2>
                <p className="text-sm text-slate-600">License: {panManagementModal.licenseKey}</p>
              </div>
              <button
                onClick={closePanManagementModal}
                className="text-slate-400 hover:text-slate-600 transition-colors"
              >
                <XCircle className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-6">
              {/* License Information */}
              <div className="bg-slate-50 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-slate-900 mb-3">License Information</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <span className="font-medium text-slate-700">Plan Type:</span>
                    <p className="text-slate-900 capitalize">{panManagementModal.licenseData?.subscription_type || 'Unknown'}</p>
                  </div>
                  <div>
                    <span className="font-medium text-slate-700">PAN Limit:</span>
                    <p className="text-slate-900">{panManagementModal.licenseData?.pan_limit || 1}</p>
                  </div>
                  <div>
                    <span className="font-medium text-slate-700">Status:</span>
                    <p className={`capitalize ${panManagementModal.licenseData?.status === 'activated' ? 'text-green-600' : 'text-slate-600'}`}>
                      {panManagementModal.licenseData?.status || 'Unknown'}
                    </p>
                  </div>
                  <div>
                    <span className="font-medium text-slate-700">Bound PANs:</span>
                    <p className="text-slate-900">{panManagementModal.boundPANs.length} / {panManagementModal.licenseData?.pan_limit || 1}</p>
                  </div>
                </div>
              </div>

              {/* Bound PANs List */}
              <div>
                <h3 className="text-lg font-semibold text-slate-900 mb-3">Bound PANs</h3>
                {panManagementModal.boundPANs.length === 0 ? (
                  <div className="text-center py-8 bg-slate-50 rounded-lg">
                    <CreditCard className="w-12 h-12 text-slate-400 mx-auto mb-3" />
                    <p className="text-slate-600">No PANs bound to this license</p>
                    <p className="text-xs text-slate-500 mt-1">PANs will appear here when users bind them through the application</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {panManagementModal.boundPANs.map((panHash, index) => (
                      <div key={panHash} className="bg-white border border-slate-200 rounded-lg p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                              <span className="text-blue-600 font-semibold text-xs">#{index + 1}</span>
                            </div>
                            <div>
                              <p className="font-medium text-slate-900">PAN Hash</p>
                              <p className="font-mono text-slate-600 text-sm">{maskPAN(panHash)}</p>
                              <p className="text-xs text-slate-500">Securely hashed and masked for privacy</p>
                            </div>
                          </div>
                          <button
                            onClick={() => handleUnbindPAN(panHash)}
                            disabled={actionLoading[`unbind-${panHash}`]}
                            className="bg-red-100 hover:bg-red-200 text-red-800 px-3 py-2 rounded-lg text-sm font-medium disabled:opacity-50 flex items-center space-x-2"
                          >
                            {actionLoading[`unbind-${panHash}`] ? (
                              <>
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600"></div>
                                <span>Unbinding...</span>
                              </>
                            ) : (
                              <>
                                <Unlink className="w-4 h-4" />
                                <span>Unbind</span>
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end space-x-3 pt-4 border-t border-slate-200">
                <button
                  onClick={closePanManagementModal}
                  className="px-4 py-2 text-slate-600 hover:text-slate-800 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPanel;